/*
 * FabScore v3 — the PCOS Check. Mounted by sections/fabscore-v3.liquid.
 *
 * THIS FILE IS A RENDERER. The backend (RtoManagement, /api/fabscore/v3/*)
 * decides everything: which question comes next, which options exist, how
 * answers are scored, which products are recommended, what they cost and what
 * her coupon is. This file draws the screen it is handed, sends back what she
 * tapped, and runs the cart calls Shopify requires to happen in the browser.
 * There are no rules, no scoring and no product list in here — so a change made
 * in the dashboard is live the moment it is published, with no theme deploy.
 *
 * Tokens are kept in localStorage and sent as headers:
 *   fs3.anonId        this browser (anonymous)
 *   fs3.attemptToken  proves this browser owns its journey  → X-Fabscore-Attempt
 *   fs3.sessionToken  proves her number is verified          → X-Fabscore-Session
 * If storage is blocked (some in-app browsers) everything still works for the
 * visit; only anonymous resume is lost, and logging in brings her journey back.
 *
 * Plain ES2017, no build step — same as the other FabScore assets.
 */
(function () {
  'use strict';

  var root = document.getElementById('fs3-root');
  if (!root) return;

  var CONFIG = {
    apiBase: (root.getAttribute('data-api-base') || '').replace(/\/$/, ''),
    shop: root.getAttribute('data-shop') || '',
    cartAddUrl: (root.getAttribute('data-cart-add-url') || '/cart/add') + '.js',
    cartUpdateUrl: (root.getAttribute('data-cart-update-url') || '/cart/update') + '.js',
    cartChangeUrl: (root.getAttribute('data-cart-change-url') || '/cart/change') + '.js',
    cartUrl: root.getAttribute('data-cart-url') || '/cart',
  };
  var API = CONFIG.apiBase + '/api/fabscore/v3';

  // The illustrations that ship with the theme, handed over by the section as a
  // name → URL map (assets/fabscore-v3-*.png). A .js asset cannot call Liquid's
  // asset_url, so this is the only way in. Missing key → the page falls back to
  // the line icon it drew before, so an older published section still renders.
  var ART = {};
  try { ART = JSON.parse(root.getAttribute('data-art-images') || '{}'); } catch (e) { ART = {}; }

  // ── storage (never throws) ────────────────────────────────────────
  var memory = {};
  var store = {
    get: function (k) { try { var v = window.localStorage.getItem(k); return v === null ? (memory[k] || null) : v; } catch (e) { return memory[k] || null; } },
    set: function (k, v) { memory[k] = v; try { window.localStorage.setItem(k, v); } catch (e) { /* in-memory only */ } },
    del: function (k) { delete memory[k]; try { window.localStorage.removeItem(k); } catch (e) { /* ignore */ } },
  };

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
  var anonId = store.get('fs3.anonId');
  if (!anonId) { anonId = uuid(); store.set('fs3.anonId', anonId); }

  function nextSeq() {
    var n = Number(store.get('fs3.clientSeq') || 0) + 1;
    store.set('fs3.clientSeq', String(n));
    return n;
  }

  // ── state ─────────────────────────────────────────────────────────
  var state = {
    view: 'boot',            // boot | landing | quiz | result
    landing: null,
    attempt: null,
    progress: null,
    screen: null,
    account: { loggedIn: false, showLogin: true, menu: [] },
    draft: [],               // selection being built on a multi-select screen
    pending: false,
    error: null,
    result: null,
    removed: {},             // productKey → true (taken out of the plan)
    sizes: {},               // productKey → variantId she picked
    picks: {},               // productKey → { diet, lifestyle } she picked in the row
    cartCount: 0,
    couponApplied: false,
  };

  // ── DOM helpers ───────────────────────────────────────────────────
  function h(tag, attrs) {
    var el = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v === null || v === undefined || v === false) return;
      if (k === 'className') el.className = v;
      else if (k === 'text') el.textContent = v;
      else if (k.indexOf('on') === 0) el.addEventListener(k.slice(2).toLowerCase(), v);
      else if (k === 'style') el.setAttribute('style', v);
      else el.setAttribute(k, v === true ? '' : v);
    });
    for (var i = 2; i < arguments.length; i += 1) {
      var child = arguments[i];
      if (child === null || child === undefined || child === false) continue;
      el.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return el;
  }

  // Copy written in the dashboard may use <strong>, <b>, <em> and <br> only.
  // Anything else is shown as text, never run.
  // Merchant HTML, from the product page's own accordions. Three rules:
  //
  //   - a tag we do not know is UNWRAPPED, not turned into its text. The theme
  //     wraps every rich-text field in <rte-formatter>, and flattening that to
  //     text took every <p> and <li> inside it with it, which is why these rows
  //     arrived as one run-on paragraph.
  //   - script and style are REMOVED outright. Their text is not content: the
  //     image blocks carry a <style> each, and textifying it printed raw CSS
  //     into the card.
  //   - everything else keeps only the attributes an image needs. Links lose
  //     their href with the tag, so nothing in here can carry her off her result.
  var BODY_TAGS = ['P', 'BR', 'UL', 'OL', 'LI', 'STRONG', 'B', 'EM', 'I', 'H3', 'H4', 'SPAN', 'DIV',
    'PICTURE', 'IMG', 'SOURCE', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH'];
  var BODY_DROP = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'IFRAME', 'LINK', 'META', 'SVG'];
  var BODY_ATTRS = { IMG: ['src', 'srcset', 'sizes', 'alt', 'width', 'height', 'loading'], SOURCE: ['srcset', 'sizes', 'media', 'type'] };

  function richBody(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html || '');
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType !== 1) return;
        if (BODY_DROP.indexOf(n.tagName) >= 0) { n.parentNode.removeChild(n); return; }
        walk(n);
        if (BODY_TAGS.indexOf(n.tagName) < 0) {
          while (n.firstChild) n.parentNode.insertBefore(n.firstChild, n);
          n.parentNode.removeChild(n);
          return;
        }
        var keep = BODY_ATTRS[n.tagName] || [];
        Array.prototype.slice.call(n.attributes).forEach(function (a) {
          if (keep.indexOf(a.name) < 0) n.removeAttribute(a.name);
        });
      });
    })(tpl.content);

    var holder = document.createElement('div');
    holder.appendChild(tpl.content);
    // The page's layout wrappers leave empty shells behind once their classes go.
    // Blocks only: a <span> holding a single space is the gap between an emoji
    // and the words after it, and dropping it ran them together.
    Array.prototype.slice.call(holder.querySelectorAll('div, p')).forEach(function (n) {
      if (!n.textContent.trim() && !n.querySelector('img')) n.parentNode.removeChild(n);
    });
    // A care symbol and its caption are one row, as the product page draws them.
    // The page nests each pair three wrappers deep for its own grid; those
    // wrappers lose their classes here, so the pair is rebuilt flat instead —
    // otherwise the symbol and its words end up on separate lines, far apart.
    Array.prototype.slice.call(holder.children).forEach(function (n) {
      var imgs = n.querySelectorAll('img');
      var text = n.textContent.replace(/\s+/g, ' ').trim();
      if (imgs.length !== 1 || !text || text.length >= 60) return;
      var img = imgs[0];
      img.removeAttribute('width');
      img.removeAttribute('height');
      var row = h('div', { className: 'fs3-pp__iconrow' }, img, h('span', { text: text }));
      n.parentNode.replaceChild(row, n);
    });
    return holder;
  }

  function rich(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html || '');
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType !== 1) return;
        if (['STRONG', 'B', 'EM', 'BR'].indexOf(n.tagName) < 0) { n.replaceWith(document.createTextNode(n.textContent)); return; }
        Array.prototype.slice.call(n.attributes).forEach(function (a) { n.removeAttribute(a.name); });
        walk(n);
      });
    })(tpl.content);
    // A <br> the stylesheet hides (the desktop science title) must not glue its
    // two halves together; the space collapses at a line start when it shows.
    Array.prototype.slice.call(tpl.content.querySelectorAll('br')).forEach(function (br) {
      var next = br.nextSibling;
      if (next && next.nodeType === 3 && !/^\s/.test(next.nodeValue)) br.parentNode.insertBefore(document.createTextNode(' '), next);
    });
    var span = document.createElement('span');
    span.appendChild(tpl.content);
    return span;
  }

  function money(n) {
    if (n === null || n === undefined) return '';
    return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  function toast(message) {
    var t = h('div', { className: 'fs3-toast', role: 'status', text: message });
    root.appendChild(t);
    setTimeout(function () { t.remove(); }, 2600);
  }

  // ── API ───────────────────────────────────────────────────────────
  function api(method, path, body) {
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'x-shopify-shop-domain': CONFIG.shop };
    var attemptToken = store.get('fs3.attemptToken');
    var sessionToken = store.get('fs3.sessionToken');
    if (attemptToken) headers['X-Fabscore-Attempt'] = attemptToken;
    if (sessionToken) headers['X-Fabscore-Session'] = sessionToken;

    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 15000) : null;

    return fetch(API + path, {
      method: method,
      headers: headers,
      credentials: 'omit',
      body: body ? JSON.stringify(body) : undefined,
      signal: controller ? controller.signal : undefined,
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (timer) clearTimeout(timer);
        var ok = res.ok && json.ok !== false;
        return { ok: ok, status: res.status, data: json.data || null, error: ok ? null : (json.error || { code: 'HTTP_' + res.status, message: json.message || 'Something went wrong' }) };
      });
    }).catch(function () {
      if (timer) clearTimeout(timer);
      return { ok: false, status: 0, data: null, error: { code: 'NETWORK', message: 'We could not reach the server. Check your connection and try again.' } };
    });
  }

  // Every quiz response has the same shape; keep what it carries.
  function absorb(data) {
    if (!data) return;
    if (data.attemptToken) store.set('fs3.attemptToken', data.attemptToken);
    if (data.session && data.session.token) store.set('fs3.sessionToken', data.session.token);
    if (data.attempt) state.attempt = data.attempt;
    if (data.account) state.account = data.account;
    if (data.progress) state.progress = data.progress;
    if (data.screen) {
      state.screen = data.screen;
      state.draft = data.screen.value && data.screen.value.optionIds ? data.screen.value.optionIds.slice() : [];
      // The second half of a date_and_select screen, kept beside the draft so a
      // re-render (tapping a chip) does not lose the date she already picked.
      state.draftText = data.screen.value && data.screen.value.text ? data.screen.value.text : '';
      state.view = data.screen.type === 'result' ? 'result' : 'quiz';
    }
    state.error = null;
  }

  // ── analytics: only what the backend cannot see ───────────────────
  var queue = [];
  // The PCOS Check WhatsApp invite's link carries a per-message token as
  // utm_content. It goes back with the first batch so the backend can mark that
  // message as landed. Only the invite's own 12-hex shape is sent: a campaign
  // name in utm_content is not a token and has nothing to match.
  var landingToken = (function () {
    var c = new URLSearchParams(window.location.search).get('utm_content');
    return c && /^[0-9a-f]{12}$/.test(c) ? c : null;
  })();
  function track(name, props, screenId) {
    queue.push({ eventId: uuid(), name: name, clientTs: new Date().toISOString(), screenId: screenId || (state.screen && state.screen.id) || null, props: props || {} });
    if (queue.length >= 20) flush();
  }
  function flush() {
    if (!queue.length && !landingToken) return;
    var batch = queue.splice(0, 50);
    var body = { anonId: anonId, events: batch };
    if (state.attempt && store.get('fs3.attemptToken')) body.attemptId = state.attempt.id;
    // Sent once. Kept back only if the request never left, so a dropped
    // connection does not lose the landing.
    var token = landingToken;
    if (token) { body.landingToken = token; landingToken = null; }
    api('POST', '/events', body).then(function (res) {
      if (res.ok || res.status !== 0) return;
      if (token) landingToken = token;
      if (queue.length < 200) queue = batch.concat(queue);
    });
  }
  setInterval(flush, 5000);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });
  window.addEventListener('pagehide', flush);

  // The storefront's own Google tag sets these; sending them lets the backend's
  // events land on the same GA4 user and session as Shopify's purchase events.
  function readGa() {
    var jar = {};
    document.cookie.split('; ').forEach(function (kv) { var i = kv.indexOf('='); if (i > 0) jar[kv.slice(0, i)] = kv.slice(i + 1); });
    var clientId = jar._ga ? jar._ga.split('.').slice(-2).join('.') : null;
    var key = Object.keys(jar).filter(function (k) { return /^_ga_[A-Z0-9]+$/.test(k); })[0];
    var raw = key ? jar[key] : '';
    var sessionId = /^GS2/.test(raw) ? ((raw.match(/s(\d+)/) || [])[1] || null) : (raw.split('.')[2] || null);
    return { clientId: clientId, sessionId: sessionId };
  }

  // The page she came from, without its query string: that can carry tokens or
  // personal details, and the domain and path are enough to name the source.
  function referrerPath() {
    try { var r = new URL(document.referrer); return r.origin + r.pathname; } catch (e) { return null; }
  }

  // Where this visit came from.
  //
  // A real utm_source always wins: the WhatsApp invite carries one, and that is
  // the reading we must never lose.
  //
  // Our OWN links (the header pill, the menu drawer) carry ?fs3_from=... instead
  // of utm_*, on purpose. A utm_ on an internal link starts a fresh GA4/Shopify
  // session and replaces the visitor's original acquisition source (Instagram,
  // an ad, search) on whatever order follows — it would quietly corrupt ad
  // reporting. fs3_from is invisible to GA4 and Shopify; only this file reads it.
  // See snippets/header-custom-button.liquid.
  function utm() {
    var p = new URLSearchParams(window.location.search);
    var out = {};
    ['source', 'medium', 'campaign', 'content', 'term'].forEach(function (k) { var v = p.get('utm_' + k); if (v) out[k] = v; });
    var from = p.get('fs3_from');
    if (!out.source && from) {
      out.source = 'website';
      out.medium = 'site_button';
      out.campaign = 'free_pcos_assessment';
      out.content = from;
    }
    return out;
  }

  // ── flow ──────────────────────────────────────────────────────────
  function clearJourney() {
    store.del('fs3.attemptToken');
    state.attempt = null; state.screen = null; state.progress = null;
  }

  function startAttempt(extra) {
    state.pending = true; render();
    var body = Object.assign({
      anonId: anonId,
      utm: utm(),
      referrer: document.referrer || null,
      landingPath: window.location.pathname,
      ga4: readGa(),
      viewport: { w: window.innerWidth, h: window.innerHeight },
    }, extra || {});
    return api('POST', '/attempts', body).then(function (res) {
      state.pending = false;
      if (!res.ok) { state.error = res.error.message; state.view = 'landing'; render(); return; }
      handleJourneyResponse(res.data);
    });
  }

  // A response may carry a screen, a resolution to choose from, or a result id.
  function handleJourneyResponse(data) {
    absorb(data);
    if (data.resultAttemptId) return openResult(data.resultAttemptId);
    if (data.resolution && data.resolution.auto === false) { render(); return openResolution(data.resolution); }
    if (state.screen && state.screen.type === 'result' && state.attempt) return openResult(state.attempt.id);
    render();
  }

  function handleError(res, retry) {
    state.pending = false;
    var code = res.error && res.error.code;
    // Two tabs, a double tap, a lost race: the server sends the right screen.
    if (res.status === 409 && res.error.details && res.error.details.screen) { absorb(res.error.details); render(); return; }
    if (code === 'FABSCORE_V3_ATTEMPT_TOKEN_INVALID' || code === 'FABSCORE_V3_ATTEMPT_EXPIRED' || code === 'FABSCORE_V3_ATTEMPT_NOT_FOUND') {
      clearJourney(); return startAttempt({ consent: consentPayload() });
    }
    if (code === 'FABSCORE_V3_UNDERAGE') { state.view = 'blocked'; state.error = (res.error.details && res.error.details.message) || res.error.message; clearJourney(); render(); return; }
    if (code === 'FABSCORE_V3_CONSENT_REQUIRED') { clearJourney(); state.view = 'landing'; }
    state.error = res.error.message;
    track('api_error_shown', { code: code, endpoint: retry || '' });
    render();
  }

  // `text` is only sent by the screens that carry a second value (a date), so
  // every existing caller keeps working unchanged.
  function submitAnswer(optionIds, text) {
    if (state.pending || !state.screen) return;
    state.pending = true; state.error = null;
    markPending();
    var screenId = state.screen.id;
    var body = { screenId: screenId, optionIds: optionIds, clientSeq: nextSeq() };
    if (text) body.text = text;
    api('POST', '/attempts/' + state.attempt.id + '/answers', body).then(function (res) {
      if (!res.ok) return handleError(res, 'answers');
      state.pending = false;
      pushHistory();
      handleJourneyResponse(res.data);
    });
  }

  function move(action, toScreenId) {
    if (state.pending || !state.screen) return;
    state.pending = true; state.error = null;
    markPending();
    api('POST', '/attempts/' + state.attempt.id + '/navigate', { action: action, fromScreenId: state.screen.id, toScreenId: toScreenId, clientSeq: nextSeq() }).then(function (res) {
      if (!res.ok) return handleError(res, 'navigate');
      state.pending = false;
      handleJourneyResponse(res.data);
    });
  }

  function markPending() {
    var card = root.querySelector('.fs3-card');
    if (card) card.classList.add('is-pending');
  }

  // The phone's back button should behave like the page's Back.
  function pushHistory() { try { window.history.pushState({ fs3: true }, ''); } catch (e) { /* ignore */ } }
  window.addEventListener('popstate', function () {
    if (state.view === 'result' && state.detailDriverId) return closeDriver();
    if (state.view === 'quiz' && state.screen && state.screen.nav && state.screen.nav.canGoBack && !state.pending) move('back');
  });

  // ── consent ───────────────────────────────────────────────────────
  var consentGiven = store.get('fs3.consentVersion');
  function consentPayload() {
    return consentGiven ? { given: true, version: Number(consentGiven) || 1 } : undefined;
  }

  // ── login (any screen) ────────────────────────────────────────────
  // While a sheet is open the page behind it must hold still.
  //
  // Two approaches were measured on the result page and both failed:
  // html[scroll-lock] (the theme's own switch in base.css) computed to
  // `overflow: hidden` and the page still scrolled under a wheel; pinning the
  // body with `position: fixed` did hold it, but took the page's height out of
  // flow, so closing the sheet dropped her at the top however the scroll was
  // put back. Blocking the gesture leaves the layout completely alone: nothing
  // moves, so there is nothing to restore. html[scroll-lock] is deliberately NOT
  // set: it did not hold the page here, and on a platform with a classic
  // scrollbar hiding it would jolt the layout sideways as the sheet opens.
  // Let the gesture through only when something under the pointer can actually
  // take it. Matching the sheet by class was not enough: the product card is
  // `overflow: hidden` with its own scroller inside, so a wheel over the card's
  // padding or its pinned close button was allowed, found nothing scrollable,
  // and ran on to scroll the page behind the sheet. overscroll-behavior in the
  // stylesheets stops a scroller that has reached its end from chaining out.
  function blockScroll(e) {
    var veil = root.querySelector('.fs3-veil');
    if (!veil) return;
    var node = e.target;
    while (node && node.nodeType === 1 && node !== veil) {
      if (node.scrollHeight > node.clientHeight) {
        var how = window.getComputedStyle(node).overflowY;
        if (how === 'auto' || how === 'scroll') return;
      }
      node = node.parentNode;
    }
    e.preventDefault();
  }
  function lockPage() {
    document.addEventListener('wheel', blockScroll, { passive: false });
    document.addEventListener('touchmove', blockScroll, { passive: false });
  }
  function unlockPage() {
    document.removeEventListener('wheel', blockScroll, { passive: false });
    document.removeEventListener('touchmove', blockScroll, { passive: false });
  }
  // Space, PageDown and the arrows scroll a page too, so they are held back the
  // same way — unless she is typing in the sheet (the login sheet has inputs).
  var SCROLL_KEYS = [' ', 'Spacebar', 'PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
  function onSheetKey(e) {
    if (e.key === 'Escape' || e.key === 'Esc') return closeSheet();
    if (SCROLL_KEYS.indexOf(e.key) < 0) return;
    var tag = e.target && e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    var veil = root.querySelector('.fs3-veil');
    if (veil && e.target && e.target.closest && e.target.closest('.fs3-pp__scroll')) return;
    e.preventDefault();
  }
  function openSheet(build) {
    closeSheet();
    var veil = h('div', { className: 'fs3-veil', role: 'dialog', 'aria-modal': 'true' });
    var sheet = h('div', { className: 'fs3-sheet' });
    veil.appendChild(sheet);
    veil.addEventListener('click', function (e) { if (e.target === veil) closeSheet(); });
    build(sheet);
    root.appendChild(veil);
    lockPage();
    document.addEventListener('keydown', onSheetKey);
    var first = sheet.querySelector('input, button');
    if (first) first.focus();
  }
  function closeSheet() {
    var v = root.querySelector('.fs3-veil');
    if (!v) return;
    v.remove();
    unlockPage();
    document.removeEventListener('keydown', onSheetKey);
  }

  function sheetHead(title) {
    return h('div', { className: 'fs3-sheet__head' },
      h('h2', { text: title }),
      h('button', { className: 'fs3-sheet__close', type: 'button', 'aria-label': 'Close', onClick: closeSheet, text: '×' }));
  }

  // One form used by the gate screen and the "Login" sheet. `onCodeSent`, when
  // given, takes over once the code is sent (the log-in-first start draws its
  // own "Verify your number" screen).
  function loginForm(container, opts) {
    var from = opts.from;
    var step = 'phone';
    var phone = '';
    var needProfile = !!opts.askProfile;
    var errorEl = h('div', { className: 'fs3-inline-error', hidden: true, role: 'alert' });
    var body = h('div');
    container.appendChild(body);
    container.appendChild(errorEl);

    function fail(msg) { errorEl.hidden = false; errorEl.textContent = msg; }
    function attemptId() { return state.attempt && store.get('fs3.attemptToken') ? state.attempt.id : undefined; }

    function drawPhone() {
      body.textContent = '';
      var nameInput = h('input', { className: 'fs3-input', id: 'fs3-name', type: 'text', autocomplete: 'name', maxlength: '60', placeholder: 'Your name' });
      var phoneInput = h('input', { className: 'fs3-input', id: 'fs3-phone', type: 'tel', inputmode: 'numeric', autocomplete: 'tel-national', maxlength: '10', placeholder: '10-digit number', value: phone });
      // The "Send me weekly check-ins on WhatsApp" tick-box used to sit here. It
      // is gone, so nothing is opted into on her behalf: `consent` is left out of
      // the payload entirely, and the backend only records a whatsapp_checkins
      // consent when that flag arrives set.
      var button = h('button', { className: 'fs3-btn fs3-btn--primary fs3-btn--block', type: 'button', text: 'Send code on WhatsApp', disabled: true });

      if (needProfile) body.appendChild(h('div', { className: 'fs3-field' }, h('label', { for: 'fs3-name', text: opts.nameLabel || 'Your name' }), nameInput));
      body.appendChild(h('div', { className: 'fs3-field' }, h('label', { for: 'fs3-phone', text: opts.phoneLabel || 'WhatsApp number' }),
        h('div', { className: 'fs3-phone' }, h('span', { className: 'fs3-phone__cc', text: '+91' }), phoneInput)));
      body.appendChild(h('div', { className: 'fs3-nav' }, button));

      // Nothing to send until the fields hold something we can send: the button
      // is dead until the number is a real 10-digit mobile (and, on a first
      // visit, a name is there), and lights up the moment they are.
      function syncSend() {
        var ok = /^[6-9]\d{9}$/.test(phoneInput.value.replace(/\D/g, '').slice(-10));
        if (needProfile) ok = ok && nameInput.value.trim().length >= 2;
        button.disabled = !ok;
      }
      [nameInput, phoneInput].forEach(function (el) { el.addEventListener('input', syncSend); });
      syncSend();

      button.addEventListener('click', function () {
        errorEl.hidden = true;
        phone = phoneInput.value.replace(/\D/g, '').slice(-10);
        if (!/^[6-9]\d{9}$/.test(phone)) return fail('Enter a valid 10-digit mobile number.');
        // anonId and the GA ids tie a login before any journey to the rest of
        // this browser's events, here and in GA4.
        var payload = { phone: phone, attemptId: attemptId(), anonId: anonId, ga4: readGa(), from: from };
        if (needProfile) {
          var name = nameInput.value.trim();
          if (name.length < 2) return fail('Please enter your name.');
          payload.name = name;
        }
        button.disabled = true;
        api('POST', '/auth/request-otp', payload).then(function (res) {
          button.disabled = false;
          if (!res.ok) return fail(res.error.message);
          step = 'otp';
          // A brand-new number needs her name. The code is already on its way,
          // so the name is asked beside it, not by sending her back to request
          // another (which hit the resend cooldown and sent a second code).
          var askName = res.data.needsProfile && !payload.name;
          if (opts.onCodeSent) return opts.onCodeSent(res.data, payload, askName);
          drawOtp(res.data, payload, askName);
        });
      });
    }

    function drawOtp(challenge, payload, askName) {
      body.textContent = '';
      var nameInput = h('input', { className: 'fs3-input', id: 'fs3-name', type: 'text', autocomplete: 'name', maxlength: '60', placeholder: 'Your name' });
      var otpInput = h('input', { className: 'fs3-input fs3-otp', id: 'fs3-otp', type: 'text', inputmode: 'numeric', autocomplete: 'one-time-code', maxlength: '6', placeholder: '••••••' });
      // Only ever present outside production, for testing without WhatsApp.
      if (challenge.devOtp) otpInput.value = challenge.devOtp;
      var button = h('button', { className: 'fs3-btn fs3-btn--primary fs3-btn--block', type: 'button', text: opts.cta || 'Verify' });
      body.appendChild(h('p', { className: 'fs3-muted', text: 'We sent a 6-digit code to ' + challenge.masked + ' on WhatsApp.' }));
      if (askName) body.appendChild(h('div', { className: 'fs3-field' }, h('label', { for: 'fs3-name', text: opts.nameLabel || 'Your name' }), nameInput));
      body.appendChild(h('div', { className: 'fs3-field' }, h('label', { for: 'fs3-otp', text: opts.otpTitle || 'Enter the code' }), otpInput));
      body.appendChild(h('div', { className: 'fs3-nav' },
        h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: 'Change number', onClick: function () { errorEl.hidden = true; drawPhone(); } }), button));
      // Same rule as the step before it: nothing to verify until six digits
      // (and, for a new number, a name) are in.
      function syncVerify() {
        var ok = otpInput.value.replace(/\D/g, '').length === 6;
        if (askName) ok = ok && nameInput.value.trim().length >= 2;
        button.disabled = !ok;
      }
      [nameInput, otpInput].forEach(function (el) { el.addEventListener('input', syncVerify); });
      syncVerify();

      button.addEventListener('click', function () {
        errorEl.hidden = true;
        var otp = otpInput.value.replace(/\D/g, '');
        if (otp.length !== 6) return fail('Enter the 6-digit code.');
        var extra = { otp: otp };
        if (askName) {
          var name = nameInput.value.trim();
          if (name.length < 2) return fail('Please enter your name.');
          extra.name = name;
        }
        button.disabled = true;
        api('POST', '/auth/verify-otp', Object.assign({}, payload, extra)).then(function (res) {
          button.disabled = false;
          if (!res.ok) return fail(res.error.message);
          closeSheet();
          state.account = Object.assign({}, state.account, { loggedIn: true, showLogin: false, firstName: res.data.customer.firstName });
          handleJourneyResponse(res.data);
        });
      });
    }
    drawPhone();
  }

  function openLogin() {
    track('login_sheet_opened', { from: state.view });
    openSheet(function (sheet) {
      sheet.appendChild(sheetHead('Login'));
      sheet.appendChild(h('p', { className: 'fs3-muted', text: 'Already taken the check? Log in to see your score and pick up where you left off.' }));
      loginForm(sheet, { from: state.view === 'landing' ? 'landing' : 'quiz_screen', askProfile: false, cta: 'Verify and continue' });
    });
  }

  // She has journeys elsewhere: let her choose, with the sensible one first.
  function openResolution(resolution) {
    var LABELS = {
      see_result: ['See my score', 'You have already completed the check.'],
      continue_other: ['Continue where I left off', resolution.otherStep ? 'You were on question ' + resolution.otherStep + ' on another device.' : 'You got further on another device.'],
      continue_here: ['Continue this check', resolution.type === 'show_result' ? 'Take it again as a new check.' : 'Keep going from this question.'],
    };
    openSheet(function (sheet) {
      sheet.appendChild(sheetHead('Welcome back'));
      resolution.choices.forEach(function (choice, i) {
        var label = LABELS[choice] || [choice, ''];
        sheet.appendChild(h('button', {
          className: 'fs3-choice' + (i === 0 ? ' fs3-choice--primary' : ''), type: 'button',
          onClick: function () {
            api('POST', '/auth/resolve', { choice: choice, attemptId: state.attempt ? state.attempt.id : undefined, anonId: anonId }).then(function (res) {
              if (!res.ok) { toast(res.error.message); return; }
              closeSheet();
              handleJourneyResponse(res.data);
            });
          },
        }, label[0], h('small', { text: label[1] })));
      });
    });
  }

  function logout() {
    api('POST', '/auth/logout').then(function () {
      store.del('fs3.sessionToken'); clearJourney();
      state.account = { loggedIn: false, showLogin: true, menu: [] };
      state.result = null; state.view = 'landing';
      loadLanding();
    });
  }

  function menuAction(item) {
    if (item === 'logout') return logout();
    if (item === 'start_new') { clearJourney(); state.result = null; return startAttempt({ restart: true, consent: consentPayload() || { given: true, version: 1 } }); }
    api('GET', '/me').then(function (res) {
      if (!res.ok || !res.data.latestResultAttemptId) return toast('No result yet.');
      if (item === 'my_result') return openResult(res.data.latestResultAttemptId);
      if (item === 'edit_answers') {
        api('POST', '/me/attempts/' + res.data.latestResultAttemptId + '/edit').then(function (edit) {
          if (!edit.ok) return toast(edit.error.message);
          state.result = null;
          handleJourneyResponse(edit.data);
        });
      }
    });
  }

  // ── result ────────────────────────────────────────────────────────
  function openResult(attemptId) {
    state.view = 'result'; state.pending = true; render();
    api('GET', '/attempts/' + attemptId + '/result').then(function (res) {
      state.pending = false;
      if (!res.ok) {
        if (res.error.code === 'FABSCORE_V3_RESULT_SUPERSEDED' && res.error.details && res.error.details.latestResultAttemptId) return openResult(res.error.details.latestResultAttemptId);
        state.view = 'landing'; state.error = res.error.message; return loadLanding();
      }
      state.result = res.data;
      state.result.attemptId = attemptId;
      if (res.data.account) state.account = res.data.account;
      state.removed = {}; state.sizes = {}; state.picks = {}; state.detailDriverId = null;
      render();
      track('gauge_viewed', { animated: true });
      var cp = res.data.coupon || {};
      noteServerClock(cp);
      if (cp.mode === 'claim') track('offer_shown', { state: cp.status });
      // Claim mode: a failed claim just shows "Claim offer" again — nothing to poll.
      if (cp.status === 'creating' || (cp.status === 'failed' && cp.mode !== 'claim')) pollCoupon(attemptId, 0);
      else if (cp.code) applyCouponIfCartHasItems(cp.code);
      syncCountdown();
    });
  }

  function pollCoupon(attemptId, tries) {
    if (!state.result || state.result.attemptId !== attemptId) return;
    if (tries >= 15) {
      // One explicit retry; after that her code shows up next time she opens the page.
      return api('POST', '/attempts/' + attemptId + '/coupon/retry').then(function (res) { if (res.ok) gotCoupon(res.data); });
    }
    setTimeout(function () {
      api('GET', '/attempts/' + attemptId + '/coupon').then(function (res) {
        if (res.ok && (res.data.code || (res.data.mode === 'claim' && res.data.status !== 'creating'))) return gotCoupon(res.data);
        pollCoupon(attemptId, tries + 1);
      });
    }, 1000);
  }
  function gotCoupon(coupon) {
    if (!state.result || (!coupon.code && coupon.mode !== 'claim')) return;
    setCoupon(coupon);
    if (coupon.code) applyCouponIfCartHasItems(coupon.code);
  }

  // ── claim offer (docs/fabscore/v3-claim-offer-plan.md) ────────────
  // Her result shows "Claim offer"; a tap makes a code that lives a few hours.
  // The timer counts from the SERVER's clock (serverNow), so a wrong phone
  // clock never shows a wrong time, and a reload continues where it was.
  var countdownTimer = null;

  function noteServerClock(coupon) {
    var t = coupon && coupon.serverNow ? Date.parse(coupon.serverNow) : NaN;
    if (!isNaN(t)) state.clockSkew = t - Date.now();
  }
  function setCoupon(coupon) {
    if (!state.result) return;
    noteServerClock(coupon);
    state.result.coupon = Object.assign({}, coupon, { message: null });
    render();
  }
  function msLeft(coupon) {
    return Date.parse(coupon.expiresAt) - (Date.now() + (state.clockSkew || 0));
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  // 03:59:59, or "2d 04:10:11" for an older 30-day code.
  function timeLeft(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var days = Math.floor(s / 86400); s %= 86400;
    var clock = pad2(Math.floor(s / 3600)) + ':' + pad2(Math.floor((s % 3600) / 60)) + ':' + pad2(s % 60);
    return days ? days + 'd ' + clock : clock;
  }
  // One timer for the page. It rewrites only the digits once a second — the
  // page itself is not redrawn — and stops as soon as there is nothing to count.
  function syncCountdown() {
    var c = state.result && state.result.coupon;
    var live = state.view === 'result' && c && c.mode === 'claim' && c.code && c.expiresAt;
    if (!live) { if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; } return; }
    if (countdownTimer) return;
    countdownTimer = setInterval(function () {
      var cc = state.result && state.result.coupon;
      if (state.view !== 'result' || !cc || !cc.expiresAt || !cc.code) { clearInterval(countdownTimer); countdownTimer = null; return; }
      var left = msLeft(cc);
      var digits = root.querySelectorAll('[data-fs3-countdown]');
      for (var i = 0; i < digits.length; i++) digits[i].textContent = timeLeft(left);
      if (left <= 0) { clearInterval(countdownTimer); countdownTimer = null; offerRanOut(cc.code); }
    }, 1000);
  }
  // Time is up: take the dead code off her bag and show "Claim offer" again.
  // The server has the last word (she may have ordered in another tab).
  function offerRanOut(code) {
    track('offer_timer_expired');
    removeCodeFromCart(code);
    state.result.coupon = Object.assign({}, state.result.coupon, { status: 'offer', code: null, expiresAt: null, canClaim: true });
    render();
    var id = state.result.attemptId;
    api('GET', '/attempts/' + id + '/coupon').then(function (res) { if (res.ok && state.result && state.result.attemptId === id) setCoupon(res.data); });
  }
  function removeCodeFromCart(code) {
    if (!code) return;
    shopifyJson('/cart.js').then(function (cart) {
      var codes = ((cart && cart.discount_codes) || []).map(function (d) { return d.code; });
      if (!codes.some(function (x) { return x.toUpperCase() === code.toUpperCase(); })) return;
      var rest = codes.filter(function (x) { return x.toUpperCase() !== code.toUpperCase(); });
      shopifyJson(CONFIG.cartUpdateUrl, { method: 'POST', body: JSON.stringify({ discount: rest.join(',') }) }).then(function (updated) { if (updated) announceCart(updated); });
    });
  }
  function claimOffer() {
    if (!state.result || state.claiming) return;
    var id = state.result.attemptId;
    state.claiming = true; track('offer_claim_clicked'); render();
    api('POST', '/attempts/' + id + '/coupon/claim').then(function (res) {
      state.claiming = false;
      if (!state.result || state.result.attemptId !== id) return;
      if (!res.ok) {
        state.result.coupon = Object.assign({}, state.result.coupon, { lastClaimFailed: true });
        render();
        return toast(res.error.message);
      }
      setCoupon(res.data);
      if (res.data.status === 'creating') return pollCoupon(id, 0);
      if (res.data.code) applyCouponIfCartHasItems(res.data.code);
    });
  }

  // ── Shopify cart (must run in the browser) ────────────────────────
  function shopifyJson(url, options) {
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }, options || {}))
      .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  function refreshCartCount() {
    shopifyJson('/cart.js').then(function (cart) { if (cart && cart.item_count !== state.cartCount) { state.cartCount = cart.item_count; render(); } });
  }
  document.addEventListener('cart:update', refreshCartCount);

  function announceCart(cart) {
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: 'fabscore-v3', data: { source: 'fabscore-v3', itemCount: cart ? cart.item_count : undefined } } }));
  }
  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open(); else window.location.href = CONFIG.cartUrl;
  }

  // Keep whatever codes are already on the cart, add hers.
  function applyToCart(attributes, code) {
    return shopifyJson('/cart.js').then(function (cart) {
      var existing = ((cart && cart.discount_codes) || []).map(function (d) { return d.code; })
        .filter(function (c) { return !code || c.toUpperCase() !== code.toUpperCase(); });
      var body = {};
      if (attributes) body.attributes = attributes;
      if (code) body.discount = existing.concat([code]).join(',');
      return shopifyJson(CONFIG.cartUpdateUrl, { method: 'POST', body: JSON.stringify(body) });
    }).then(function (cart) {
      if (code) {
        var applied = !!cart && (cart.discount_codes || []).some(function (d) { return d.code.toUpperCase() === code.toUpperCase(); });
        state.couponApplied = applied;
        track(applied ? 'coupon_applied_to_cart' : 'coupon_apply_failed', applied ? {} : { reason: cart ? 'not_on_cart' : 'request_failed' });
      }
      return cart;
    });
  }

  function applyCouponIfCartHasItems(code) {
    shopifyJson('/cart.js').then(function (cart) {
      if (cart && cart.item_count > 0) applyToCart(null, code).then(function (updated) { if (updated) { announceCart(updated); render(); } });
    });
  }

  // Does the cart line already there disagree with the properties the server
  // just built for it? Only what the server sends is compared: Shopify adds
  // nothing of its own, but a future property we do not manage must not make
  // every tap rewrite the line.
  function stalePropsOn(line, wanted) {
    if (!line) return false;
    var on = line.properties || {};
    return Object.keys(wanted).some(function (name) { return String(on[name] == null ? '' : on[name]) !== String(wanted[name]); });
  }

  // ── the product popup ─────────────────────────────────────────────
  // Figma "NEW PRODUCT POPUP" (node 1861-22267). A plan row opens a card that
  // only DESCRIBES the product: hero, name, price and the accordions. Nothing
  // in it changes the plan and nothing leaves for the PDP — size, colour and
  // the Diet Plan's two pickers all stay on the row itself.

  // The two plan covers are the Figma drawings the theme ships (the products
  // carry no photo of their own).
  function planCover(item, images) {
    var covers = { standard_diet_plan: (images || {}).planDiet, standard_movement_plan: (images || {}).planMovement };
    return covers[item.key] || null;
  }
  // Those same two are the PDFs, and only they wear Figma's "E-copy" badge.
  function isEcopy(item) { return item.key === 'standard_diet_plan' || item.key === 'standard_movement_plan'; }

  // Shopify's own product JSON, fetched on first open and kept for the page's
  // life. The popup paints before it lands, from what the result API already
  // sent, so there is never a spinner over the parts we know.
  var productCache = {};
  function loadProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) productCache[handle] = shopifyJson('/products/' + encodeURIComponent(handle) + '.js');
    return productCache[handle];
  }

  // The accordion rows Figma shows beyond the description — Details,
  // Ingredients, How to use, Wash + Care, Packer & Manufacturer Details, and
  // the two plans' questions — are the SAME rows the product's own page shows.
  // Most of them are not on the product at all: they are blocks authored per
  // template in the theme editor, which no other template (and no product API)
  // can read. So the popup reads the product page itself and lifts its
  // accordions, and whatever the PDP shows, the popup shows.
  //
  // Both PDP families use a machine-readable shape: the shared accordion block
  // (blocks/_accordion-row.liquid) renders `details[data-testid=…]`, and the two
  // plan pages render `details.plan-pdp__faq`. DOMParser runs no scripts and
  // fetches no images, so the cost is the HTML alone — once per product, kept
  // for the life of the page.
  var extrasCache = {};
  function loadProductExtras(handle) {
    if (!handle) return Promise.resolve(null);
    if (!extrasCache[handle]) {
      extrasCache[handle] = fetch('/products/' + encodeURIComponent(handle), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) { return html ? pdpContentIn(html) : null; })
        .catch(function () { return null; });
    }
    return extrasCache[handle];
  }

  // The chevron and plus glyphs live inside the summary, so they are taken out
  // before its words are read. A row whose body is empty is dropped, the same
  // rule the block itself follows.
  var ACCORDION_CHROME = 'svg, .svg-wrapper, .details__icon, .plan-pdp__faq-toggle';
  function pdpContentIn(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var out = [];
    // The two plan pages write their own description (sections/plan-product.liquid
    // resolves it from custom.plan_description, falling back to the per-plan
    // default). Shopify's product `description` for these two is the one-line
    // "sent as PDFs on WhatsApp" note, which is not what their page — or Figma —
    // shows, so the page's own paragraph wins when there is one.
    var lead = doc.querySelector('.plan-pdp__description');
    // The plan pages carry a shorter, cleaner name than the Shopify product
    // ("Hormonal Balanced Diet Plan" against "… (PDF only)"), resolved from
    // custom.short_title. Figma shows that one, so the page's wins.
    var name = doc.querySelector('.plan-pdp__title');
    var take = function (head, body) {
      if (!head || !body) return;
      var label = head.cloneNode(true);
      Array.prototype.slice.call(label.querySelectorAll(ACCORDION_CHROME)).forEach(function (n) { n.parentNode.removeChild(n); });
      var title = (label.textContent || '').replace(/\s+/g, ' ').trim();
      var inner = body.innerHTML || '';
      if (!title || !inner.replace(/<[^>]*>/g, '').trim()) return;
      out.push({ title: title, html: inner });
    };
    Array.prototype.slice.call(doc.querySelectorAll('details[data-testid="accordion-details"]')).forEach(function (d) {
      take(d.querySelector('summary'), d.querySelector('.details-content'));
    });
    Array.prototype.slice.call(doc.querySelectorAll('details.plan-pdp__faq')).forEach(function (d) {
      take(d.querySelector('.plan-pdp__faq-question'), d.querySelector('.plan-pdp__faq-answer'));
    });
    return {
      title: name ? (name.textContent || '').replace(/\s+/g, ' ').trim() : null,
      description: lead ? lead.innerHTML : null,
      sections: out,
    };
  }

  // The two plans are PDFs, so Figma gives them an icon strip the physical
  // products do not have. These labels are the design's own words and belong to
  // no product record, so they live here — and they paint with the card rather
  // than waiting on a page-sized fetch, which is what makes the card feel whole
  // the moment it opens.
  //
  // `title` is the name their own page shows (sections/plan-product.liquid's
  // per-plan default). Shopify's product title for these two ends in
  // "(PDF only)", so without it the card opened on one name and swapped to
  // another a second and a half later, once the page arrived. Painting the
  // page's own name straight away makes that swap a no-op — and a merchant who
  // sets custom.short_title still wins, because the page's value is applied
  // over this one either way.
  //
  // The icons are the exact files the plan PDP draws (ART, from
  // sections/fabscore-v3.liquid's art_images map) — not redrawn line art, so
  // the popup and the PDP can never show two different marks for "PDF copy".
  var POPUP_EXTRAS = {
    standard_diet_plan: {
      title: 'Hormonal Balanced Diet Plan',
      trust: [['plan_delivery_no_physical', 'No physical product'], ['plan_delivery_pdf', 'PDF copy'], ['plan_delivery_whatsapp', 'WhatsApp delivery']],
    },
    standard_movement_plan: {
      title: 'Hormonal Balanced Exercise Plan',
      features: [['plan_move_cycle', 'Planned around your cycle'], ['plan_move_calendar', 'No equipment'], ['plan_move_no_equipment', 'Beginner-friendly'], ['plan_move_home', 'Do it at home']],
      trust: [['plan_delivery_no_physical', 'No physical product'], ['plan_delivery_pdf', 'PDF copy'], ['plan_delivery_whatsapp', 'WhatsApp delivery']],
    },
  };

  function featureStrip(list, className) {
    var strip = h('div', { className: className });
    list.forEach(function (pair) {
      var icon = h('span', { className: 'fs3-pp__fic' });
      if (ART[pair[0]]) icon.appendChild(h('img', { src: ART[pair[0]], alt: '', loading: 'lazy' }));
      strip.appendChild(h('div', { className: 'fs3-pp__f' }, icon, h('span', { text: pair[1] })));
    });
    return strip;
  }

  function shareProduct(item) {
    var url = item.handle ? (window.location.origin + '/products/' + item.handle) : window.location.href;
    if (navigator.share) return navigator.share({ title: item.title, url: url }).catch(function () {});
    if (navigator.clipboard) return navigator.clipboard.writeText(url).then(function () { toast('Link copied.'); }, function () {});
    return toast(url);
  }

  // One accordion row. Closed rows cost nothing until she opens them, and a
  // row with no words does not render at all (Figma hides, never shows empty).
  function popupRow(title, fill, open) {
    var sign = h('span', { className: 'fs3-pp__sign', 'aria-hidden': 'true' });
    var row = h('details', { className: 'fs3-pp__row' });
    if (open) row.setAttribute('open', 'open');
    var paint = function () { sign.innerHTML = ''; artInto(sign, (window.FS3ResultArt || {})[row.open ? 'minus' : 'plus']); };
    row.appendChild(h('summary', { className: 'fs3-pp__sum' }, h('span', { text: title }), sign));
    row.appendChild(fill);
    row.addEventListener('toggle', paint);
    paint();
    return row;
  }

  function openProductSheet(item, images) {
    track('product_popup_opened', { product_key: item.key });
    openSheet(function (sheet) {
      sheet.classList.add('fs3-pp');
      var R = window.FS3ResultArt || {};

      var hero = h('div', { className: 'fs3-pp__hero' });
      var cover = planCover(item, images) || item.image;
      var img = h('img', { alt: '', loading: 'lazy' });
      if (cover) img.src = cover;
      hero.appendChild(img);
      if (isEcopy(item)) hero.appendChild(h('span', { className: 'fs3-pp__badge', text: 'E-copy' }));
      // The close button is the card's, not the hero's: the card scrolls and a
      // ✕ that scrolled away would leave her with only the scrim and Esc — on a
      // phone the scrim is a thin margin. At rest it sits exactly where Figma
      // draws it, over the hero's top-right corner.
      var close = h('button', { className: 'fs3-pp__close', type: 'button', 'aria-label': 'Close', onClick: closeSheet });
      artInto(close, R.close);

      var title = h('h2', { className: 'fs3-pp__title', text: (POPUP_EXTRAS[item.key] || {}).title || item.title });
      var head = h('div', { className: 'fs3-pp__head' }, title);
      if (item.handle || navigator.share) {
        var share = h('button', { className: 'fs3-pp__share', type: 'button', 'aria-label': 'Share ' + item.title, onClick: function () { shareProduct(item); } });
        artInto(share, R.share);
        head.appendChild(share);
      }

      var was = item.compareAtPrice;
      var cheaper = was && was > item.price;
      var price = h('div', { className: 'fs3-pp__price' }, h('span', { className: 'fs3-pp__now', text: money(item.price) }),
        cheaper ? h('span', { className: 'fs3-pp__was', text: money(was) }) : null,
        cheaper ? h('span', { className: 'fs3-pp__off', text: Math.round((1 - item.price / was) * 100) + '% OFF' }) : null);

      var body = h('div', { className: 'fs3-pp__body' }, head);
      // The row's own subheading — the same short line, same source
      // (item.description), the plan row already shows under its title.
      // Every product gets it: Figma's popup frames show it on every card.
      var about = cleanCopy(item.description);
      if (about) body.appendChild(h('p', { className: 'fs3-pp__desc', text: about }));
      body.appendChild(price);
      var rows = h('div', { className: 'fs3-pp__rows' });
      body.appendChild(rows);
      // Figma puts the strips between the price and the first accordion.
      var extras = POPUP_EXTRAS[item.key] || {};
      if ((extras.features || []).length) body.insertBefore(featureStrip(extras.features, 'fs3-pp__feats'), rows);
      if ((extras.trust || []).length) body.insertBefore(featureStrip(extras.trust, 'fs3-pp__feats fs3-pp__feats--trust'), rows);

      var scroller = h('div', { className: 'fs3-pp__scroll' }, hero, body);
      sheet.appendChild(scroller);
      sheet.appendChild(close);

      // Description first, then the rows the product's own page shows. Figma
      // orders these differently per product — one order everywhere keeps the
      // popup predictable.
      //
      // Two sources fill the description and the slower, richer one wins: the
      // product JSON arrives first so she is not left looking at nothing, and
      // the product page's own paragraph replaces it when it lands. An empty
      // source never clears what is already there.
      var descRow = null;
      var setDescription = function (html) {
        var content = richBody(html);
        if (!content.textContent.trim()) return;
        content.className = 'fs3-pp__rich';
        if (descRow) { descRow.replaceChild(content, descRow.lastChild); return; }
        descRow = popupRow('Description', content, true);
        rows.insertBefore(descRow, rows.firstChild);
      };

      // The photo is the one fast thing here, so it is swapped on its own
      // rather than behind the page-sized fetch.
      loadProduct(item.handle).then(function (product) {
        if (!product || !sheet.isConnected) return;
        if (!planCover(item, images) && product.featured_image) img.src = product.featured_image;
      });

      Promise.all([loadProduct(item.handle), loadProductExtras(item.handle)]).then(function (both) {
        if (!sheet.isConnected) return;
        var product = both[0];
        var extra = both[1] || {};
        if (extra.title) title.textContent = extra.title;

        // The rows are the product page's own rows, in its own order. Only the
        // two plans get a "Description" of their own: their page carries that
        // paragraph as body copy, and Figma draws it as the card's first row.
        // A page that already lists its accordions (the panties, the wipes, the
        // serum) needs no such row — Figma draws only the rows it has.
        if (extra.description) setDescription(extra.description);
        (extra.sections || []).forEach(function (section) {
          var content = richBody(section.html);
          if (!content.textContent.trim()) return;
          content.className = 'fs3-pp__rich';
          rows.appendChild(popupRow(section.title, content, false));
        });

        // A page that gave us nothing at all still owes her something: Shopify's
        // own product description, which /products/<handle>.js puts in
        // `description` (`body_html` comes back empty there).
        if (!rows.children.length && product) setDescription(product.description || product.body_html);
      });


    });
  }

  function addPlanToCart(thenCheckout) {
    if (state.pending || !state.result) return;
    // Rows "included with" another product ride on that product's cart line.
    var items = state.result.plan.items.filter(function (i) { return !i.included && !state.removed[i.key]; })
      .map(function (i) {
        // Both pickers always travel together — one alone cannot name a plan,
        // and the backend would fall back to her answers (or the default).
        var personalize;
        (i.personalize || []).forEach(function (picker) {
          personalize = personalize || {};
          personalize[picker.id] = (state.picks[i.key] || {})[picker.id] || picker.selected;
        });
        return { productKey: i.key, variantId: state.sizes[i.key] || undefined, personalize: personalize };
      });
    if (!items.length) return toast('Add at least one item to your plan.');

    state.pending = true; render();
    api('POST', '/attempts/' + state.result.attemptId + '/cart', { items: items, source: 'result' }).then(function (res) {
      if (!res.ok) { state.pending = false; render(); return toast(res.error.message); }
      var payload = res.data;
      // Claim mode: "Get my plan" may have just claimed her offer — start the timer.
      if (payload.coupon && payload.coupon.mode === 'claim') { noteServerClock(payload.coupon); state.result.coupon = Object.assign({}, payload.coupon, { message: null }); }
      // Do not add a plan item twice if she taps again.
      return shopifyJson('/cart.js').then(function (cart) {
        var have = {};
        ((cart && cart.items) || []).forEach(function (line) { var k = line.properties && line.properties._fabscore_key; if (k) have[k] = line; });
        var fresh = payload.items.filter(function (line) { return !have[line.properties._fabscore_key]; });
        // A line she added earlier keeps the properties it went in with. If she
        // then changed a picker, the cart would still carry the old Diet /
        // Lifestyle and the old PDF would be delivered — so those lines are
        // rewritten in place first, by line KEY (one variant can hold several
        // lines, and a numeric id would be read as a line index). One at a
        // time: concurrent writes to the same cart lose each other.
        var restale = payload.items.filter(function (line) { return stalePropsOn(have[line.properties._fabscore_key], line.properties); });
        // A rewrite that quietly failed would send her the plan she no longer
        // wants, so it fails the whole tap rather than going on to checkout.
        var failedAt = null;
        var add = restale.reduce(function (chain, line) {
          return chain.then(function () {
            return shopifyJson(CONFIG.cartChangeUrl, {
              method: 'POST',
              body: JSON.stringify({ id: have[line.properties._fabscore_key].key, properties: line.properties }),
            }).then(function (changed) { if (!changed) failedAt = failedAt || 'cart/change'; });
          });
        }, Promise.resolve()).then(function () {
          if (failedAt) return null;
          return fresh.length ? shopifyJson(CONFIG.cartAddUrl, { method: 'POST', body: JSON.stringify({ items: fresh }) }) : {};
        });
        return add.then(function (added) {
          if (!added) { state.pending = false; render(); track('api_error_shown', { code: 'CART_ADD', endpoint: failedAt || 'cart/add' }); return toast('We could not add your plan to the cart. Please try again.'); }
          return applyToCart(payload.attributes, payload.discountCode).then(function (updated) {
            state.pending = false;
            // The plan is in the Shopify cart now (this was checkout_clicked,
            // though nothing here opens checkout). Shopify prices are in paise.
            track('plan_added_to_cart', {
              item_count: payload.items.length,
              total: updated && typeof updated.total_price === 'number' ? updated.total_price / 100 : null,
            });
            flush();
            announceCart(updated);
            render();
            if (thenCheckout) window.location.href = '/checkout'; else openCartDrawer();
          });
        });
      });
    });
  }

  // ── render ────────────────────────────────────────────────────────
  function topbar(opts) {
    opts = opts || {};
    var actions = h('div', { className: 'fs3-topbar__actions' });
    if (state.account && state.account.loggedIn) {
      var wrap = h('div', { className: 'fs3-menu__wrap' });
      var initial = (state.account.firstName || 'Y').charAt(0).toUpperCase();
      wrap.appendChild(h('button', { className: 'fs3-iconbtn', type: 'button', 'aria-label': 'Your account', 'aria-haspopup': 'menu', text: initial, onClick: function () {
        var open = wrap.querySelector('.fs3-menu');
        if (open) return open.remove();
        track('profile_menu_opened');
        var LABEL = { my_result: 'My result', edit_answers: 'Edit my answers', start_new: 'Start a new check', logout: 'Log out' };
        var menu = h('div', { className: 'fs3-menu', role: 'menu' });
        (state.account.menu || []).forEach(function (item) {
          menu.appendChild(h('button', { type: 'button', role: 'menuitem', text: LABEL[item] || item, onClick: function () { menu.remove(); menuAction(item); } }));
        });
        wrap.appendChild(menu);
      } }));
      actions.appendChild(wrap);
    } else if (state.account && state.account.showLogin) {
      actions.appendChild(h('button', { className: 'fs3-link', type: 'button', text: opts.loginLabel || 'Login', onClick: openLogin }));
    }
    if (state.cartCount > 0) {
      // A drawn bag, not the 🛍 emoji: the emoji's width differs by device (iOS
      // Safari drew it wide enough to push the button off the header's edge).
      var cartBtn = h('button', { className: 'fs3-iconbtn', type: 'button', 'aria-label': 'Your cart', onClick: openCartDrawer });
      cartBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 8h12l1 12H5L6 8z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M9 10V7a3 3 0 0 1 6 0v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
      cartBtn.appendChild(h('span', { className: 'fs3-iconbtn__count', text: String(state.cartCount) }));
      actions.appendChild(cartBtn);
    }
    return actions;
  }

  // ── landing page ──────────────────────────────────────────────────
  // The page follows the Figma "New Landing page" frames (fabscore-v3-landing.css
  // carries the measurements, fabscore-v3-landing-art.js the vectors). Every word
  // comes from the backend (`landing.copy`); pictures come from the section
  // (`data-landing-images`: the persona drawings, the science photo, the phone
  // mock-up, the logo and the FAQ row icons).
  function art(html, tag) { var el = document.createElement(tag || 'span'); el.innerHTML = html || ''; return el.firstElementChild || el; }
  function artInto(el, html) { var t = document.createElement('template'); t.innerHTML = html || ''; el.appendChild(t.content); return el; }
  // rich() returns a wrapper span; the landing CSS styles the cleaned nodes
  // directly (h1 > em, .lp-note > br), so they are moved out of it.
  function richInto(el, html) { var holder = rich(html); while (holder.firstChild) el.appendChild(holder.firstChild); return el; }
  function lpText(tag, className, html) { return richInto(h(tag, className ? { className: className } : {}), html); }

  // Every "start" on the page goes through here. Consent is asked once, in a
  // small sheet, the first time she starts — the landing itself stays clean.
  function startFromLanding(entry, after) {
    var consent = (state.landing || {}).consent || {};
    var needsConsent = consent.requireBeforeFirstAnswer !== false && !consentGiven;
    // Dashboard switch: log in with her WhatsApp number before the first
    // question. Already logged in on this browser: nothing to ask.
    var loginFirst = !!(((state.landing || {}).login || {}).requireBeforeStart) && !store.get('fs3.sessionToken');
    // The press itself. landing_cta_click (in go) fires only after she agrees,
    // so on its own it cannot show who pressed Start and left at the consent.
    track('landing_start_clicked', { cta_position: entry, consent_needed: needsConsent });
    var go = function () {
      track('landing_cta_click', { cta_position: entry });
      startAttempt({ consent: { given: true, version: Number(consentGiven) || consent.version || 1 }, entryPoint: entry }).then(function () { if (after) after(); });
    };
    if (!needsConsent && !loginFirst) return go();
    var cc = consent.copy || {};
    // After logging in first: a check she left half-done carries on, one with a
    // result offers her score or a new check, anything else starts fresh.
    var startAfterLogin = function (data) {
      if (data.resultAttemptId) { absorb(data); return openStartChoice(data.resultAttemptId); }
      if (data.screen) return handleJourneyResponse(data);
      absorb(data);
      go();
    };
    var openStartChoice = function (resultAttemptId) {
      openSheet(function (sheet) {
        sheet.appendChild(sheetHead('Welcome back'));
        [
          ['See my score', 'You have already completed the check.', function () { openResult(resultAttemptId); }],
          ['Start a new check', 'Take it again from the first question.', function () {
            clearJourney(); state.result = null;
            track('landing_cta_click', { cta_position: entry });
            startAttempt({ restart: true, consent: consentPayload() || { given: true, version: consent.version || 1 }, entryPoint: entry });
          }],
        ].forEach(function (choice, i) {
          sheet.appendChild(h('button', {
            className: 'fs3-choice' + (i === 0 ? ' fs3-choice--primary' : ''), type: 'button',
            onClick: function () { closeSheet(); choice[2](); },
          }, choice[0], h('small', { text: choice[1] })));
        });
      });
    };
    // Leaving without agreeing: the × or a tap outside, on the consent sheet or
    // on the "how we use your information" sheet opened from it.
    var leave = function () { track('consent_closed', { cta_position: entry }); };
    var trackOutsideTap = function (sheet) {
      var veil = sheet.parentNode;
      veil.addEventListener('click', function (e) { if (e.target === veil) leave(); });
    };
    var openConsent = function () {
      openSheet(function (sheet) {
        sheet.classList.add('fs3-sheet--consent');
        trackOutsideTap(sheet);
        var inner = h('div', { className: 'fs3-consent__in' }); sheet.appendChild(inner);
        var box = h('input', { type: 'checkbox', id: 'fs3-age', className: 'fs3-consent__box' });
        // Nothing to confirm yet, so there is nothing to agree to: the button is
        // dead until she ticks the age box, and lights up the moment she does.
        var agree = h('button', { className: 'fs3-consent__cta', type: 'button', disabled: true, onClick: function () {
          if (!box.checked) return;
          consentGiven = String(consent.version || 1); store.set('fs3.consentVersion', consentGiven);
          track('consent_agreed', { cta_position: entry });
          closeSheet(); go();
        } });
        agree.textContent = cleanCopy(cc.cta) || 'I agree — start my check';
        box.addEventListener('change', function () { agree.disabled = !box.checked; });
        var link = h('button', { className: 'fs3-consent__link', type: 'button', onClick: openInfo });
        link.appendChild(document.createTextNode(cleanCopy(cc.infoLink) || 'How we use your information'));
        artInto(link, (window.FS3LandingArt || {}).arrow || '');
        sheet.appendChild(h('button', { className: 'fs3-sheet__close fs3-consent__close', type: 'button', 'aria-label': 'Close', onClick: function () { leave(); closeSheet(); }, text: '×' }));
        inner.appendChild(h('h2', { className: 'fs3-consent__title', text: cleanCopy(cc.title) || 'Before we start' }));
        inner.appendChild(lpText('p', 'fs3-consent__intro', cleanCopy(cc.intro) || 'Your answers help us create a personalised HealthFab plan for you. We keep your information private and do not sell your personal data.'));
        inner.appendChild(h('label', { className: 'fs3-consent__age', for: 'fs3-age' }, box, h('span', {}, cleanCopy(cc.ageLabel) || 'I confirm that I am 18 years or older.', h('i', { className: 'fs3-consent__req', 'aria-hidden': 'true', text: ' *' }))));
        inner.appendChild(agree);
        inner.appendChild(lpText('p', 'fs3-consent__foot', cleanCopy(cc.footnote) || 'By continuing, you agree to HealthFab processing your answers to provide your assessment and personalised plan.'));
        inner.appendChild(link);
      });
    };
    // "How we use your information": the backend's `consent.copy.info` (sections
    // of { title, text, items }) when the dashboard has it, else the words below.
    var openInfo = function () {
      var info = Array.isArray(cc.info) && cc.info.length ? cc.info : CONSENT_INFO;
      openSheet(function (sheet) {
        sheet.classList.add('fs3-sheet--consent', 'fs3-sheet--info');
        trackOutsideTap(sheet);
        var inner = h('div', { className: 'fs3-consent__in' }); sheet.appendChild(inner);
        sheet.appendChild(h('button', { className: 'fs3-sheet__close fs3-consent__close', type: 'button', 'aria-label': 'Close', onClick: function () { leave(); closeSheet(); }, text: '×' }));
        inner.appendChild(h('h2', { className: 'fs3-consent__title fs3-consent__title--info', text: cleanCopy(cc.infoTitle) || 'How we use your information' }));
        inner.appendChild(lpText('p', 'fs3-consent__intro fs3-consent__lead', cleanCopy(cc.infoIntro) || 'HealthFab uses the information you provide to understand your health patterns and create your personalised HealthFab experience.'));
        info.forEach(function (sec, i) {
          var block = h('section', { className: 'fs3-info' });
          if (sec.title) block.appendChild(h('h3', { className: 'fs3-info__title' }, h('span', { className: 'fs3-info__num', text: String(i + 1) }), sec.title));
          if (sec.text) block.appendChild(lpText('p', 'fs3-info__text', sec.text));
          if (sec.items && sec.items.length) {
            var ul = h('ul', { className: 'fs3-info__list' });
            sec.items.forEach(function (it) { ul.appendChild(lpText('li', '', it)); });
            block.appendChild(ul);
          }
          if (sec.after) block.appendChild(lpText('p', 'fs3-info__text', sec.after));
          inner.appendChild(block);
        });
        inner.appendChild(h('button', { className: 'fs3-consent__cta', type: 'button', text: cleanCopy(cc.infoBack) || 'Back', onClick: function () { backFromInfo(); } }));
      });
    };
    // "Back" on the information sheet returns to whichever sheet opened it.
    var backFromInfo = openConsent;

    // Log-in-first, step 1: the plain Login sheet (number only).
    var openStartLogin = function () {
      track('login_sheet_opened', { from: 'landing' });
      openSheet(function (sheet) {
        sheet.appendChild(sheetHead('Login'));
        sheet.appendChild(h('p', { className: 'fs3-muted', text: 'Log in with your WhatsApp number to start your check.' }));
        loginForm(sheet, { from: 'landing', askProfile: false, onCodeSent: openVerify });
      });
    };
    // Step 2: "Verify your number" — her name (new number only), the code, the
    // age box and the consent words. Pressing the button is agreeing.
    var openVerify = function (challenge, payload, askName) {
      var typed = { name: '', otp: challenge.devOtp || '', age: false };
      if (needsConsent) track('consent_viewed', { cta_position: entry });
      var draw = function () {
        openSheet(function (sheet) {
          sheet.classList.add('fs3-sheet--consent');
          if (needsConsent) trackOutsideTap(sheet);
          var inner = h('div', { className: 'fs3-consent__in' }); sheet.appendChild(inner);
          var nameInput = h('input', { className: 'fs3-input', id: 'fs3-name', type: 'text', autocomplete: 'name', maxlength: '60', placeholder: 'Your name', value: typed.name });
          // Only ever pre-filled outside production, for testing without WhatsApp.
          var otpInput = h('input', { className: 'fs3-input fs3-otp', id: 'fs3-otp', type: 'text', inputmode: 'numeric', autocomplete: 'one-time-code', maxlength: '6', placeholder: '••••••', value: typed.otp });
          var box = h('input', { type: 'checkbox', id: 'fs3-age', className: 'fs3-consent__box' });
          box.checked = typed.age;
          var errorEl = h('div', { className: 'fs3-inline-error', hidden: true, role: 'alert' });
          var button = h('button', { className: 'fs3-consent__cta', type: 'button', disabled: true, text: needsConsent ? (cleanCopy(cc.verifyCta) || 'I agree — continue') : 'Verify and continue' });
          var link = h('button', { className: 'fs3-consent__link', type: 'button', onClick: function () { backFromInfo = draw; openInfo(); } });
          link.appendChild(document.createTextNode(cleanCopy(cc.infoLink) || 'How we use your information'));
          artInto(link, (window.FS3LandingArt || {}).arrow || '');

          sheet.appendChild(h('button', { className: 'fs3-sheet__close fs3-consent__close', type: 'button', 'aria-label': 'Close', onClick: function () { if (needsConsent) leave(); closeSheet(); }, text: '×' }));
          inner.appendChild(h('h2', { className: 'fs3-consent__title fs3-consent__title--verify', text: 'Verify your number' }));
          inner.appendChild(h('p', { className: 'fs3-consent__intro', text: 'We’ve sent a 6-digit code to your WhatsApp number ending in ' + payload.phone.slice(-4) + '.' }));
          if (askName) inner.appendChild(h('div', { className: 'fs3-field' }, h('label', { for: 'fs3-name', text: 'Your name' }), nameInput));
          inner.appendChild(h('div', { className: 'fs3-field' }, h('label', { for: 'fs3-otp', text: 'Enter the code' }), otpInput));
          inner.appendChild(h('button', { className: 'fs3-consent__change', type: 'button', text: 'Change number', onClick: openStartLogin }));
          if (needsConsent) {
            inner.appendChild(h('hr', { className: 'fs3-consent__rule' }));
            inner.appendChild(h('label', { className: 'fs3-consent__age', for: 'fs3-age' }, box, h('span', {}, cleanCopy(cc.ageLabel) || 'I confirm that I am 18 years or older.', h('i', { className: 'fs3-consent__req', 'aria-hidden': 'true', text: ' *' }))));
          }
          inner.appendChild(button);
          inner.appendChild(errorEl);
          inner.appendChild(lpText('p', 'fs3-consent__foot', cleanCopy(cc.footnote) || 'By continuing, you agree to HealthFab processing your answers to provide your assessment and personalised plan.'));
          inner.appendChild(link);

          // Nothing to press until the code (and her name, and the age box,
          // when they are asked) are in.
          var sync = function () {
            typed = { name: nameInput.value, otp: otpInput.value, age: box.checked };
            var ok = otpInput.value.replace(/\D/g, '').length === 6;
            if (askName) ok = ok && nameInput.value.trim().length >= 2;
            if (needsConsent) ok = ok && box.checked;
            button.disabled = !ok;
          };
          [nameInput, otpInput].forEach(function (el) { el.addEventListener('input', sync); });
          box.addEventListener('change', sync);
          sync();

          button.addEventListener('click', function () {
            errorEl.hidden = true;
            var fail = function (msg) { errorEl.hidden = false; errorEl.textContent = msg; };
            var otp = otpInput.value.replace(/\D/g, '');
            if (otp.length !== 6) return fail('Enter the 6-digit code.');
            var extra = { otp: otp };
            if (askName) {
              if (nameInput.value.trim().length < 2) return fail('Please enter your name.');
              extra.name = nameInput.value.trim();
            }
            if (needsConsent && !box.checked) return fail('Please confirm that you are 18 years or older.');
            if (needsConsent && !consentGiven) {
              consentGiven = String(consent.version || 1); store.set('fs3.consentVersion', consentGiven);
              track('consent_agreed', { cta_position: entry });
            }
            button.disabled = true;
            api('POST', '/auth/verify-otp', Object.assign({}, payload, extra)).then(function (res) {
              button.disabled = false;
              if (!res.ok) return fail(res.error.message);
              closeSheet();
              state.account = Object.assign({}, state.account, { loggedIn: true, showLogin: false, firstName: res.data.customer.firstName });
              startAfterLogin(res.data);
            });
          });
        });
      };
      draw();
    };

    if (loginFirst) return openStartLogin();
    // Counted once: going to the information sheet and back reopens it.
    track('consent_viewed', { cta_position: entry });
    openConsent();
  }

  // The words behind "How we use your information", until the dashboard sends its own.
  var CONSENT_INFO = [
    { title: 'What information do we collect?', text: 'Depending on what you choose to provide, this may include:', items: [
      'Basic information: age/date of birth, name, contact details',
      'Period &amp; cycle information: period dates, cycle length, period symptoms and related answers',
      'Health &amp; lifestyle information: answers about sleep, movement, nutrition, digestion, skin and other health-related areas included in the assessment',
      'Assessment information: your answers and your resulting HealthFabScore',
      'Usage information: information about how you use our website/app, where applicable',
    ] },
    { title: 'Why do we use it?', text: 'We use your information to:', items: [
      'Calculate your HealthFabScore',
      'Understand the health patterns reflected in your answers',
      'Create your personalised plan and recommendations',
      'Help you track your progress, where those features are enabled',
      'Provide and improve HealthFab’s services',
      'Respond to your requests and support queries',
    ] },
    { title: 'Who do we share it with?', text: 'We share information only with service providers who help us operate HealthFab, such as hosting, analytics, communications and technology providers. They process information on our behalf for the purposes described above.' },
    { title: 'Do we sell your information?', text: 'We do not sell your personal data.' },
    { title: 'How long do we keep it?', text: 'We keep your personal information only for as long as necessary to provide the services you use, fulfil the purposes described above, meet applicable legal requirements, or resolve disputes.' },
    { title: 'Your choices and rights', text: 'You can:', items: [
      'Withdraw your consent',
      'Request access to your personal data',
      'Request correction of inaccurate information',
      'Request deletion of your personal data, where applicable',
      'Raise a grievance or complaint',
    ] },
  ];

  function renderLanding() {
    var landing = state.landing || {};
    var c = landing.copy || {};
    var A = window.FS3LandingArt;
    if (!c.hero || !A) return renderLandingBasic();

    var images = {};
    try { images = JSON.parse(root.getAttribute('data-landing-images') || '{}'); } catch (e) { images = {}; }
    var picture = function (key, alt, cls) {
      return images[key] ? h('img', { src: images[key], alt: alt || '', loading: 'lazy', className: cls || '' }) : h('span', { className: 'fs3-lp-noimg', 'aria-hidden': 'true' });
    };
    var cta = function (label, entry, cls, onClick) {
      var b = h('button', { className: 'lp-cta' + (cls ? ' ' + cls : ''), type: 'button', onClick: onClick || function () { startFromLanding(entry); } });
      richInto(b, label); artInto(b, A.arrow); return b;
    };
    var jump = function (id) { var el = root.querySelector('#fs3-' + id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
    var inner = function (cls, children) {
      var el = h('div', { className: 'lp-in' + (cls ? ' ' + cls : '') });
      children.forEach(function (child) { if (child) el.appendChild(child); });
      return el;
    };
    var section = function (id, className, children) {
      return h('section', { className: className, id: 'fs3-' + id }, inner(className + '__in', children));
    };
    // Section headings. The Figma frame draws two kinds: eyebrow + title (how,
    // why) and title + soft subtitle (everything else) — `sub` picks the second.
    var head = function (eyebrow, title, sub) {
      var el = h('div', { className: 'lp-head' });
      if (eyebrow) el.appendChild(lpText('div', 'lp-eyebrow', eyebrow));
      if (title) el.appendChild(lpText('h2', 'lp-h2', title));
      if (sub) el.appendChild(lpText('p', 'lp-sub', sub));
      return el;
    };

    // header — logo, section links, her account/cart, the main button
    var nav = h('nav', { className: 'lp-nav', 'aria-label': 'Sections' });
    (c.nav.links || []).forEach(function (l) { nav.appendChild(h('a', { href: '#fs3-' + l.target, text: l.label, onClick: function (e) { e.preventDefault(); jump(l.target); } })); });
    var logo = h('a', { className: 'lp-logo', href: '/', 'aria-label': 'HealthFab' },
      images.logo ? h('img', { src: images.logo, alt: 'HealthFab', width: 194, height: 32 }) : h('span', { className: 'lp-logo__text' }, 'Health', h('b', { text: 'fab' })));
    var header = h('header', { className: 'lp-header' }, h('div', { className: 'lp-header__in' }, logo, nav,
      h('div', { className: 'lp-header__right' }, topbar(), cta(c.nav.cta, 'nav', 'lp-cta--nav'))));

    // hero
    var meta = h('div', { className: 'lp-meta' });
    (c.hero.meta || []).forEach(function (text, i) { var m = h('span', { className: 'lp-meta__item' }); artInto(m, (A.meta || [])[i]); m.appendChild(richInto(h('span'), text)); meta.appendChild(m); });
    var badgeRow = h('div', { className: 'lp-badge__row' });
    artInto(badgeRow, A.laurel); badgeRow.appendChild(h('b', { text: c.hero.badge.value })); artInto(badgeRow, String(A.laurel || '').replace('<svg ', '<svg class="flip" '));
    var heroError = state.error ? h('div', { className: 'fs3-inline-error', role: 'alert', text: state.error }) : null;
    var hero = h('section', { className: 'lp-hero', id: 'fs3-hero' }, h('div', { className: 'lp-hero__in' },
      h('div', { className: 'lp-hero__copy' }, lpText('h1', 'lp-hero__title', c.hero.title), lpText('p', 'lp-hero__sub', c.hero.subtitle), cta(c.hero.cta, 'hero', 'lp-cta--hero'), heroError, meta),
      h('div', { className: 'lp-hero__visual' },
        images.phone ? h('img', { className: 'lp-phone', src: images.phone, alt: 'Your FabScore result on a phone', width: 687, height: 1358 }) : null,
        h('div', { className: 'lp-badge' }, badgeRow, h('span', { className: 'lp-badge__text' }, rich(c.hero.badge.text))))));

    // how it works — three circles on a wave
    var steps = h('div', { className: 'lp-steps' });
    artInto(steps, String(A.wave || '').replace('<svg ', '<svg class="lp-steps__wave" '));
    [1, 2].forEach(function (i) { var a = h('span', { className: 'lp-steps__arrow is-' + i, 'aria-hidden': 'true' }); artInto(a, A.stepArrow); steps.appendChild(a); });
    (c.how.steps || []).slice(0, 3).forEach(function (st, i) {
      var ic = h('span', { className: 'lp-step__ic' }); artInto(ic, (A.steps || [])[i]);
      steps.appendChild(h('div', { className: 'lp-step is-' + (i + 1) }, ic, lpText('h3', '', st.title), lpText('p', '', st.text)));
    });

    // why it matters — the four overlapping patterns
    var venn = h('div', { className: 'lp-venn', role: 'img', 'aria-label': c.why.diagramLabel || '' });
    var patterns = (c.why.patterns || []).slice(0, 4);
    patterns.forEach(function (pt, i) { venn.appendChild(h('div', { className: 'lp-venn__c is-' + (i + 1) })); });
    patterns.forEach(function (pt, i) { var lab = h('div', { className: 'lp-venn__lab is-' + (i + 1) }); artInto(lab, (A.patterns || [])[i]); lab.appendChild(h('b', { text: pt.title })); lab.appendChild(h('span', { text: pt.text })); venn.appendChild(lab); });
    var mid = h('div', { className: 'lp-venn__mid' }); artInto(mid, A.centre); mid.appendChild(lpText('b', '', c.why.centre)); venn.appendChild(mid);

    // what are you curious about
    var chosen = state.landingInterests || [];
    var count = h('span', { className: 'lp-curious__count' });
    var goOn = cta(c.curious.cta, 'curious', 'lp-cta--continue', function () {
      state.landingInterests = chosen;
      track('landing_interests_selected', { interests: chosen });
      startFromLanding('curious');
    });
    var chipEls = [];
    var syncCurious = function () {
      count.textContent = chosen.length ? String(c.curious.selectedCount || '{count} selected').replace('{count}', chosen.length) : (c.curious.emptyCount || '');
      goOn.disabled = chosen.length === 0;
      chipEls.forEach(function (pair) { var on = chosen.indexOf(pair.id) >= 0; pair.el.classList.toggle('on', on); pair.el.setAttribute('aria-pressed', on); });
    };
    var chips = h('div', { className: 'lp-chips' });
    (c.curious.chips || []).forEach(function (chip) {
      var drawing = (A.chips || {})[chip.id] || {};
      var el = h('button', { className: 'lp-chip', type: 'button', style: drawing.style || '', onClick: function () {
        if (chosen.indexOf(chip.id) >= 0) chosen = chosen.filter(function (x) { return x !== chip.id; });
        else if (chip.id === c.curious.exclusiveChipId) chosen = [chip.id];
        else chosen = chosen.filter(function (x) { return x !== c.curious.exclusiveChipId; }).concat(chip.id);
        syncCurious();
      } });
      var tick = h('span', { className: 'tick' }); artInto(tick, drawing.tick); el.appendChild(tick);
      artInto(el, drawing.icon); el.appendChild(h('span', { className: 'lp-chip__label', text: chip.label }));
      chipEls.push({ id: chip.id, el: el }); chips.appendChild(el);
    });
    syncCurious();

    // whether you're…  — a card answers the first question for her
    var personaGrid = h('div', { className: 'lp-persona-grid' });
    (c.personas.items || []).forEach(function (pn, i) {
      personaGrid.appendChild(h('button', { className: 'lp-persona', type: 'button', onClick: function () {
        startFromLanding('persona_' + (i + 1), function () {
          var sc = state.screen;
          var offered = sc && !sc.value && (sc.options || []).some(function (o) { return o.id === pn.stageOptionId; });
          if (state.view === 'quiz' && offered) submitAnswer([pn.stageOptionId]);
        });
      } }, picture('persona_' + (i + 1), ''), lpText('span', 'pt', pn.title), lpText('span', 'pd', pn.text)));
    });

    // backed by science — four trust marks, the button and the photo with its handwritten note
    var trust = h('div', { className: 'lp-trust' });
    (c.science.trust || []).forEach(function (t, i) { var ic = h('span', { className: 'lp-trust__ic' }); artInto(ic, (A.trust || [])[i]); trust.appendChild(h('div', { className: 'lp-trust__item' }, ic, richInto(h('span'), t))); });
    // No note in the content means no note at all — an empty one would still show its heart doodle.
    var noteText = c.science.note || (c.personas || {}).note;
    var note = null;
    if (noteText) { note = h('div', { className: 'lp-note hand', 'aria-hidden': 'true' }); richInto(note, noteText); artInto(note, A.heart); }
    var scienceBottom = h('div', { className: 'lp-science__bottom' }, cta(c.science.cta, 'science', 'lp-cta--science'),
      h('div', { className: 'lp-science__photo' }, note, picture('science', c.science.imageAlt)));

    // real stories — no frame in the Figma file; drawn only when the dashboard has some
    var stories = null;
    var storyItems = (c.stories || {}).items || [];
    if (storyItems.length) {
      var scroll = h('div', { className: 'lp-stories__scroll' });
      storyItems.forEach(function (st, i) {
        var drawing = (A.stories || [])[i % ((A.stories || []).length || 1)] || {};
        scroll.appendChild(h('div', { className: 'lp-story' }, art(drawing.stars, 'div'), h('p', { className: 'lp-story__quote', text: '“' + st.quote + '”' }),
          h('div', { className: 'lp-story__foot' }, h('span', { className: 'lp-story__avatar', style: drawing.avatarStyle || '', text: String(st.name || '').charAt(0) }),
            h('span', {}, h('span', { className: 'lp-story__name', text: st.name }), h('span', { className: 'lp-story__metric', text: st.metric })))));
      });
      stories = section('stories', 'lp-stories', [head(null, c.stories.title || c.stories.eyebrow, null), scroll]);
    }

    // faqs — one row per question, the Figma icons cycle by position
    var faqList = h('div', { className: 'lp-faq__list' });
    var faqIcons = images.faq || [];
    (c.faqs.items || []).forEach(function (f, i) {
      var ic = h('span', { className: 'lp-faq__ic', 'aria-hidden': 'true' });
      if (faqIcons.length) ic.appendChild(h('img', { src: faqIcons[i % faqIcons.length], alt: '', width: 30, height: 30, loading: 'lazy' }));
      var chev = h('span', { className: 'lp-faq__chev', 'aria-hidden': 'true' }); artInto(chev, A.chev);
      var sum = h('summary', {}, ic, richInto(h('span', { className: 'lp-faq__q' }), f.q), chev);
      faqList.appendChild(h('details', { className: 'lp-faq__item' }, sum, h('div', { className: 'lp-faq__a' }, lpText('p', '', f.a))));
    });

    var page = h('div', { className: 'fs3-lp' }, header, hero,
      section('how', 'lp-how', [head(c.how.eyebrow, c.how.title), steps]),
      section('patterns', 'lp-why', [
        h('div', { className: 'lp-why__copy' }, head(c.why.eyebrow, c.why.title, c.why.subtitle)),
        venn,
        // `ctaTarget` is a section id to scroll to, or "start" to open the check
        // itself — set in the pack, so which one this button does is edited there.
        cta(c.why.cta, 'why', 'lp-cta--why', function () {
          var target = c.why.ctaTarget || 'faqs';
          if (target === 'start') return startFromLanding('why');
          jump(target);
        })]),
      section('curious', 'lp-curious', [head(null, c.curious.title, c.curious.help), chips,
        h('div', { className: 'lp-curious__foot' }, count, goOn)]),
      section('personas', 'lp-personas', [head(null, c.personas.eyebrow, c.personas.title), personaGrid]),
      section('approach', 'lp-science', [head(null, c.science.title, c.science.subtitle), trust, scienceBottom]),
      stories,
      section('faqs', 'lp-faq', [head(null, c.faqs.eyebrow, c.faqs.title), faqList]),
      c.footer ? h('footer', { className: 'lp-foot' }, inner('', [h('span', { text: c.footer })])) : null);
    return page;
  }

  // The plain landing: used only while a shop's published content predates the
  // full landing page (no `copy.hero`), so the page never breaks mid-rollout.
  function renderLandingBasic() {
    var landing = state.landing || {};
    var copy = landing.copy || {};
    var consent = landing.consent || {};
    var needsConsent = consent.requireBeforeFirstAnswer !== false && !consentGiven;
    var box = h('input', { type: 'checkbox', id: 'fs3-consent' });
    var start = h('button', { className: 'fs3-btn fs3-btn--accent', type: 'button', text: copy.cta || 'Take the free check' });
    var error = h('div', { className: 'fs3-inline-error', hidden: !state.error, role: 'alert', text: state.error || '' });
    // Same rule as the consent sheet: no tick, no button. (It used to be clickable
    // and answer with "Please agree to continue.")
    if (needsConsent) {
      start.disabled = true;
      box.addEventListener('change', function () { start.disabled = !box.checked; });
    }

    start.addEventListener('click', function () {
      if (needsConsent && !box.checked) return;
      // Here the consent is a checkbox on the page, not a sheet, so there is no
      // consent_viewed or consent_closed: Start only works once it is ticked.
      track('landing_start_clicked', { cta_position: 'hero', consent_needed: needsConsent });
      if (needsConsent) {
        consentGiven = String(consent.version || 1); store.set('fs3.consentVersion', consentGiven);
        track('consent_agreed', { cta_position: 'hero' });
      }
      track('landing_cta_click', { cta_position: 'hero', cta_text: start.textContent });
      startAttempt({ consent: { given: true, version: Number(consentGiven) || consent.version || 1 } });
    });

    var page = h('div', { className: 'fs3-landing' },
      h('div', { className: 'fs3-topbar', style: 'justify-content:space-between' }, h('div', { className: 'fs3-wm' }, 'Health', h('b', { text: 'fab' })), topbar()),
      h('p', { className: 'fs3-eyebrow', text: 'HealthFab · PCOS Check' }),
      h('h1', {}, rich(cleanCopy(copy.title) || 'What’s been bothering you lately?')),
      h('p', { className: 'fs3-landing__sub' }, rich(cleanCopy(copy.subtitle) || 'Answer a few questions about your cycle, symptoms and routine. We’ll show you what’s driving them and what tends to help. It is not a diagnosis.')));

    if (needsConsent) {
      var cc = consent.copy || {};
      page.appendChild(h('label', { className: 'fs3-check', for: 'fs3-consent' }, box,
        h('span', {}, rich(cleanCopy(cc.checkboxLabel) || 'I agree to HealthFab using my answers to build my plan. My answers stay private and are never sold.'))));
    }
    page.appendChild(h('div', { className: 'fs3-landing__cta' }, start, h('span', { className: 'fs3-muted', text: landing.progressLabel || '' }), error));

    if ((landing.drivers || []).length) {
      var grid = h('div', { className: 'fs3-driver-grid' });
      landing.drivers.forEach(function (d) {
        grid.appendChild(h('div', { className: 'fs3-driver-tile', style: 'border-top:4px solid ' + (d.color || 'var(--fs3-accent)') },
          h('b', { text: d.label }), h('span', { text: cleanCopy(d.summary) || '' })));
      });
      page.appendChild(grid);
    }
    return page;
  }

  // Placeholder copy from the dashboard is never shown to a customer.
  function cleanCopy(text) { return text && String(text).indexOf('TODO-COPY') === 0 ? '' : text; }
  // Coupon copy comes from the backend with a {percent} slot, so the wording can
  // never disagree with the discount the backend actually created.
  function fillPercent(text, percent) { return String(text || '').replace('{percent}', percent === undefined || percent === null ? '' : String(percent)); }

  function optionButton(option, selected, multi, rank, onClick) {
    return h('button', {
      className: 'fs3-opt' + (multi ? ' fs3-opt--multi' : ''), type: 'button',
      role: multi ? 'checkbox' : 'radio',
      'aria-checked': multi ? null : String(selected),
      'aria-pressed': multi ? String(selected) : null,
      onClick: onClick,
    },
    h('span', {}, h('span', { className: 'fs3-opt__label', text: option.label }), option.sub ? h('span', { className: 'fs3-opt__sub', text: option.sub }) : null),
    // The mark is Figma's 22px ring: empty until picked, then a filled dot (radio)
    // or a tick (checkbox). Ranked picks print their number instead.
    h('span', { className: 'fs3-opt__mark', 'aria-hidden': 'true', text: selected && rank ? String(rank) : '' }));
  }

  // A local Y/M/D as YYYY-MM-DD, without the UTC round-trip toISOString()
  // does — that shifts the date near midnight in a timezone ahead of UTC.
  function localIsoDate(date) {
    var y = date.getFullYear();
    var m = ('0' + (date.getMonth() + 1)).slice(-2);
    var d = ('0' + date.getDate()).slice(-2);
    return y + '-' + m + '-' + d;
  }

  // Turn a YYYY-MM-DD date into whichever of the screen's options her age in
  // completed years falls into (the same band ids `chips` used to submit
  // directly). Returns null for a blank, future or unreadable date so the
  // caller can show a validation message instead of guessing.
  function dobToBand(screen, isoDate) {
    if (!isoDate) return null;
    var dob = new Date(isoDate + 'T00:00:00');
    if (isNaN(dob.getTime())) return null;
    var today = new Date();
    if (dob > today) return null;

    var age = today.getFullYear() - dob.getFullYear();
    var hadBirthdayThisYear = today.getMonth() > dob.getMonth()
      || (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
    if (!hadBirthdayThisYear) age -= 1;

    var band = (screen.options || []).find(function (o) {
      var min = o.minAge === undefined || o.minAge === null ? -Infinity : o.minAge;
      var max = o.maxAge === undefined || o.maxAge === null ? Infinity : o.maxAge;
      return age >= min && age <= max;
    });
    return band ? band.id : null;
  }

  // Every browser draws an empty date input differently: Chrome prints
  // dd/mm/yyyy, Safari prints today's date (which reads as already answered),
  // iOS prints nothing at all. So while the field is empty and unfocused the
  // browser's own text is hidden and ours is drawn instead — the same on every
  // device. On focus the native text comes back, so typing still works.
  function dateField(input) {
    var hint = h('span', { className: 'fs3-date__hint', 'aria-hidden': 'true', text: 'dd/mm/yyyy' });
    var pic = h('span', { className: 'fs3-date__ic', 'aria-hidden': 'true' });
    pic.innerHTML = '<svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6">'
      + '<rect x="2.5" y="4" width="15" height="13" rx="2.5"/><path d="M2.5 8h15M7 2.5v3M13 2.5v3"/></svg>';
    var box = h('span', { className: 'fs3-date' }, input, hint, pic);
    var sync = function () { box.className = 'fs3-date' + (input.value ? ' has-value' : ''); };
    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
    input.addEventListener('blur', sync);
    sync();
    return box;
  }

  // Date of birth: the same box as every other date field, but our own calendar
  // instead of the browser's. A native <input type="date"> opens a picker drawn
  // by the browser — it lands on the current month and carries a "Today" button,
  // and neither can be styled or removed from the page. On a date of birth both
  // are wrong, so the picker below is ours: it opens on DEFAULT_DOB_YEAR, has no
  // "Today", and puts month and year on dropdowns so a birth year is one tap
  // away. The field still takes typing (dd/mm/yyyy). The "last period" screen
  // keeps the native input — there today IS the likely answer.
  var DEFAULT_DOB_YEAR = 2000;
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // 'dd/mm/yyyy' → 'yyyy-mm-dd', or '' when it is not a real date. The
  // round-trip through Date() is what rejects 31/02/2000.
  function dmyToIso(text) {
    var m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(text || '').trim());
    if (!m) return '';
    var d = Number(m[1]); var mo = Number(m[2]); var y = Number(m[3]);
    var dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return '';
    return m[3] + '-' + ('0' + mo).slice(-2) + '-' + ('0' + d).slice(-2);
  }
  function pad2(n) { return ('0' + n).slice(-2); }

  function dobField(onChange) {
    var thisYear = new Date().getFullYear();
    var input = h('input', {
      className: 'fs3-input', id: 'fs3-dob', type: 'text',
      inputmode: 'numeric', autocomplete: 'bday', maxlength: '10',
      'aria-label': 'Date of birth, dd/mm/yyyy',
    });
    var hint = h('span', { className: 'fs3-date__hint', 'aria-hidden': 'true', text: 'dd/mm/yyyy' });
    var icon = h('button', { className: 'fs3-date__ic', type: 'button', 'aria-label': 'Choose a date', 'aria-expanded': 'false' });
    icon.innerHTML = '<svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6">'
      + '<rect x="2.5" y="4" width="15" height="13" rx="2.5"/><path d="M2.5 8h15M7 2.5v3M13 2.5v3"/></svg>';
    var panel = h('div', { className: 'fs3-cal', hidden: true });
    var box = h('div', { className: 'fs3-date' }, input, hint, icon, panel);

    var view = { y: DEFAULT_DOB_YEAR, m: 0 };
    function sync() { box.className = 'fs3-date' + (input.value ? ' has-value' : ''); }

    // Typing: digits only, slashes placed for her.
    input.addEventListener('input', function () {
      var digits = input.value.replace(/\D/g, '').slice(0, 8);
      var out = digits.slice(0, 2);
      if (digits.length > 2) out += '/' + digits.slice(2, 4);
      if (digits.length > 4) out += '/' + digits.slice(4, 8);
      input.value = out;
      sync(); onChange();
    });

    // Three panes behind one popup: days, then the month list, then the year
    // list. The month and year were <select>s at first, and a native select on a
    // phone opens a full-height OS list over the page — the thing this picker
    // exists to avoid. Tapping the title walks back: days → months → years.
    var mode = 'days';

    function header(title, onTitle, onPrev, onNext) {
      function step(delta, label, path) {
        var b = h('button', { className: 'fs3-cal__nav', type: 'button', 'aria-label': label });
        b.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="' + path + '"/></svg>';
        b.addEventListener('click', delta);
        return b;
      }
      var head = h('div', { className: 'fs3-cal__head' });
      head.appendChild(onPrev ? step(onPrev, 'Previous', 'M10 3 5 8l5 5') : h('span'));
      var t = h('button', { className: 'fs3-cal__title', type: 'button', text: title });
      if (onTitle) { t.addEventListener('click', onTitle); t.innerHTML += '<svg viewBox="0 0 12 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 1.75 6 6.25l4.5-4.5"/></svg>'; }
      else t.disabled = true;
      head.appendChild(t);
      head.appendChild(onNext ? step(onNext, 'Next', 'M6 3l5 5-5 5') : h('span'));
      panel.appendChild(head);
    }

    function drawDays() {
      header(MONTHS[view.m] + ' ' + view.y, function () { mode = 'years'; drawPanel(); },
        function () { shiftMonth(-1); }, function () { shiftMonth(1); });
      var grid = h('div', { className: 'fs3-cal__grid' });
      ['S', 'M', 'T', 'W', 'T', 'F', 'S'].forEach(function (d) { grid.appendChild(h('span', { className: 'fs3-cal__dow', text: d })); });
      var first = new Date(view.y, view.m, 1).getDay();
      var days = new Date(view.y, view.m + 1, 0).getDate();
      for (var b = 0; b < first; b += 1) grid.appendChild(h('span'));
      var chosen = dmyToIso(input.value);
      for (var d = 1; d <= days; d += 1) {
        (function (day) {
          var iso = view.y + '-' + pad2(view.m + 1) + '-' + pad2(day);
          var cell = h('button', { className: 'fs3-cal__day', type: 'button', text: String(day) });
          // A date of birth cannot be in the future; those days show but are dead.
          if (new Date(view.y, view.m, day) > new Date()) cell.disabled = true;
          if (iso === chosen) cell.setAttribute('aria-current', 'date');
          cell.addEventListener('click', function () {
            input.value = pad2(day) + '/' + pad2(view.m + 1) + '/' + view.y;
            sync(); close(); onChange(); input.focus();
          });
          grid.appendChild(cell);
        }(d));
      }
      panel.appendChild(grid);
    }

    function drawMonths() {
      header(String(view.y), function () { mode = 'years'; drawPanel(); },
        view.y > thisYear - 100 ? function () { view.y -= 1; drawPanel(); } : null,
        view.y < thisYear ? function () { view.y += 1; drawPanel(); } : null);
      var grid = h('div', { className: 'fs3-cal__months' });
      MONTHS.forEach(function (name, i) {
        var b = h('button', { className: 'fs3-cal__cell', type: 'button', text: name.slice(0, 3) });
        if (new Date(view.y, i, 1) > new Date()) b.disabled = true;
        if (i === view.m) b.setAttribute('aria-current', 'true');
        b.addEventListener('click', function () { view.m = i; mode = 'days'; drawPanel(); });
        grid.appendChild(b);
      });
      panel.appendChild(grid);
    }

    function drawYears() {
      header('Select year', null, null, null);
      var grid = h('div', { className: 'fs3-cal__years' });
      var mark = null;
      for (var y = thisYear; y >= thisYear - 100; y -= 1) {
        (function (yy) {
          var b = h('button', { className: 'fs3-cal__cell', type: 'button', text: String(yy) });
          if (yy === view.y) { b.setAttribute('aria-current', 'true'); mark = b; }
          b.addEventListener('click', function () { view.y = yy; mode = 'months'; drawPanel(); });
          grid.appendChild(b);
        }(y));
      }
      panel.appendChild(grid);
      // Open on the year she is already looking at (2000 by default), not at the
      // top of a hundred-year list.
      if (mark) grid.scrollTop = Math.max(0, mark.offsetTop - grid.clientHeight / 2 + mark.offsetHeight / 2);
    }

    function shiftMonth(delta) {
      var t = view.m + delta;
      view.y += Math.floor(t / 12); view.m = ((t % 12) + 12) % 12;
      if (view.y > thisYear) { view.y = thisYear; view.m = 11; }
      if (view.y < thisYear - 100) { view.y = thisYear - 100; view.m = 0; }
      drawPanel();
    }

    function drawPanel() {
      panel.textContent = '';
      if (mode === 'years') drawYears();
      else if (mode === 'months') drawMonths();
      else drawDays();
    }

    // Redrawing a pane detaches the button that was clicked, so by the time the
    // event reaches the document box.contains(e.target) is already false and the
    // popup would close itself. Stop in-popup clicks before they get there.
    panel.addEventListener('click', function (e) { e.stopPropagation(); });
    function onDocClick(e) { if (!box.contains(e.target)) close(); }
    function onKey(e) { if (e.key === 'Escape') close(); }
    function open() {
      var iso = dmyToIso(input.value);
      // Reopen where she is, otherwise on the default birth year — never today.
      view = iso ? { y: Number(iso.slice(0, 4)), m: Number(iso.slice(5, 7)) - 1 } : { y: DEFAULT_DOB_YEAR, m: 0 };
      mode = 'days';
      // Visible first: a hidden panel measures zero, and the year list centres
      // itself on the year in view by measuring.
      panel.hidden = false; icon.setAttribute('aria-expanded', 'true');
      drawPanel();
      document.addEventListener('click', onDocClick); document.addEventListener('keydown', onKey);
    }
    function close() {
      panel.hidden = true; icon.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDocClick); document.removeEventListener('keydown', onKey);
    }
    icon.addEventListener('click', function (e) { e.stopPropagation(); if (panel.hidden) open(); else close(); });

    sync();
    box.value = function () { return dmyToIso(input.value); };
    box.destroy = close;
    return box;
  }

  function renderDateOfBirth(screen) {
    var card = h('div', { className: 'fs3-card' + (state.pending ? ' is-pending' : '') });
    if (screen.eyebrow) card.appendChild(h('p', { className: 'fs3-eyebrow', text: screen.eyebrow }));
    card.appendChild(h('h2', { className: 'fs3-q' }, rich(screen.title)));
    if (screen.help) card.appendChild(h('p', { className: 'fs3-help' }, rich(screen.help)));

    var field = dobField(function () { syncNext(); if (state.error) { state.error = null; render(); } });

    var nav = h('div', { className: 'fs3-nav fs3-nav--sticky' });
    if (screen.nav && screen.nav.canGoBack) nav.appendChild(h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: 'Back', onClick: function () { move('back'); } }));
    var next = primaryButton((screen.nav && screen.nav.nextLabel) || 'Continue', true, function () {
      var band = dobToBand(screen, field.value());
      if (!band) { state.error = 'Please enter your date of birth.'; return render(); }
      submitAnswer([band]);
    });
    nav.appendChild(next);
    // Continue stays off until she has a date, the same as every other screen.
    // Toggled here rather than by re-rendering, so the field keeps focus.
    function syncNext() { next.disabled = state.pending || !dobToBand(screen, field.value()); }
    syncNext();

    card.appendChild(h('div', { className: 'fs3-field' }, h('label', { for: 'fs3-dob', text: 'Date of birth' }), field));
    if (state.error) card.appendChild(h('div', { className: 'fs3-inline-error', role: 'alert', text: state.error }));
    card.appendChild(nav);
    return card;
  }

  // Two answers on one card: the day her last period started and the usual gap
  // between periods. Same card, eyebrow, question, chips and nav as every other
  // screen — only the two fields inside are new.
  function renderDateAndSelect(screen) {
    var dateCfg = screen.date || {};
    var selectCfg = screen.select || {};
    var card = h('div', { className: 'fs3-card' + (state.pending ? ' is-pending' : '') });

    if (screen.eyebrow) card.appendChild(h('p', { className: 'fs3-eyebrow', text: screen.eyebrow }));
    card.appendChild(h('h2', { className: 'fs3-q' }, rich(screen.title)));
    if (screen.help) card.appendChild(h('p', { className: 'fs3-help' }, rich(screen.help)));

    var today = new Date();
    var oldest = new Date(today.getTime() - (Number(dateCfg.maxDaysAgo) || 730) * 86400000);
    var input = h('input', {
      className: 'fs3-input', id: 'fs3-date', type: 'date',
      max: localIsoDate(today), min: localIsoDate(oldest), value: state.draftText || '',
    });
    input.addEventListener('input', function () {
      state.draftText = input.value;
      if (state.error) { state.error = null; }
      render();
    });
    card.appendChild(h('div', { className: 'fs3-field' },
      dateCfg.label ? h('label', { for: 'fs3-date', text: dateCfg.label }) : null, dateField(input)));

    var chips = h('div', { className: 'fs3-chips', role: 'radiogroup', 'aria-label': selectCfg.label || 'Options' });
    (screen.options || []).forEach(function (option) {
      var selected = state.draft.indexOf(option.id) >= 0;
      chips.appendChild(h('button', {
        className: 'fs3-chip', type: 'button', role: 'radio', 'aria-checked': String(selected),
        text: option.label,
        onClick: function () { state.draft = [option.id]; render(); },
      }));
    });
    // Same field treatment as the date above it, so the card reads as two
    // fields rather than two unrelated blocks.
    card.appendChild(h('div', { className: 'fs3-field' },
      selectCfg.label ? h('label', { text: selectCfg.label }) : null,
      selectCfg.help ? h('span', { className: 'fs3-help', text: selectCfg.help }) : null,
      chips));

    if (state.error) card.appendChild(h('div', { className: 'fs3-inline-error', role: 'alert', text: state.error }));

    var ready = !!state.draftText && state.draft.length === 1;
    var nav = h('div', { className: 'fs3-nav fs3-nav--sticky' });
    if (screen.nav && screen.nav.canGoBack) nav.appendChild(h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: 'Back', onClick: function () { move('back'); } }));
    nav.appendChild(primaryButton((screen.nav && screen.nav.nextLabel) || 'Continue', !ready || state.pending, function () { submitAnswer(state.draft.slice(), state.draftText); }));
    card.appendChild(nav);
    return card;
  }

  function renderQuestion(screen) {
    var sel = screen.selection || { min: 1, max: 1, exclusiveOptionIds: [] };
    var multi = ['multi_select', 'grouped_multi_select', 'ranked_select'].indexOf(screen.type) >= 0;
    var asChips = screen.type === 'chips' || screen.type === 'grouped_multi_select';
    var card = h('div', { className: 'fs3-card' + (state.pending ? ' is-pending' : '') });

    if (screen.eyebrow) card.appendChild(h('p', { className: 'fs3-eyebrow', text: screen.eyebrow }));
    card.appendChild(h('h2', { className: 'fs3-q' }, rich(screen.title)));
    if (screen.help) card.appendChild(h('p', { className: 'fs3-help' }, rich(screen.help)));

    function toggle(id) {
      // One tap answers a single-choice question: the pink ring shows for the
      // moment the request takes, then the next screen arrives. (Figma draws a
      // Continue button here too; the merchant chose the faster one-tap flow.)
      if (!multi) { state.draft = [id]; render(); return submitAnswer([id]); }
      var exclusive = sel.exclusiveOptionIds || [];
      var i = state.draft.indexOf(id);
      if (i >= 0) state.draft.splice(i, 1);
      else if (exclusive.indexOf(id) >= 0) state.draft = [id];
      else {
        state.draft = state.draft.filter(function (x) { return exclusive.indexOf(x) < 0; });
        state.draft.push(id);
        if (sel.max && state.draft.length > sel.max) state.draft.shift();       // the oldest pick makes room
      }
      render();
    }

    function draw(options, holder) {
      options.forEach(function (option) {
        var selected = state.draft.indexOf(option.id) >= 0;
        var rank = sel.ranked && selected ? state.draft.indexOf(option.id) + 1 : null;
        if (asChips) {
          holder.appendChild(h('button', { className: 'fs3-chip', type: 'button', 'aria-pressed': multi ? String(selected) : null, 'aria-checked': multi ? null : String(selected), role: multi ? 'checkbox' : 'radio',
            onClick: function () { toggle(option.id); }, text: (rank ? rank + '. ' : '') + option.label }));
        } else holder.appendChild(optionButton(option, selected, multi, rank, function () { toggle(option.id); }));
      });
    }

    if (screen.groups && screen.groups.length) {
      var grouped = {};
      screen.groups.forEach(function (g) {
        var holder = h('div', { className: 'fs3-chips', role: 'group', 'aria-label': g.label });
        draw(screen.options.filter(function (o) { grouped[o.id] = grouped[o.id] || g.optionIds.indexOf(o.id) >= 0; return g.optionIds.indexOf(o.id) >= 0; }), holder);
        card.appendChild(h('div', { className: 'fs3-group' }, h('p', { className: 'fs3-group__label', text: g.label }), holder));
      });
      var rest = screen.options.filter(function (o) { return !grouped[o.id]; });
      if (rest.length) { var restHolder = h('div', { className: 'fs3-chips' }); draw(rest, restHolder); card.appendChild(h('div', { className: 'fs3-group fs3-group--rest' }, restHolder)); }
    } else {
      var holder = h('div', { className: asChips ? 'fs3-chips' : 'fs3-options', role: multi ? 'group' : 'radiogroup', 'aria-label': 'Options' });
      draw(screen.options || [], holder);
      card.appendChild(holder);
    }

    if (state.error) card.appendChild(h('div', { className: 'fs3-inline-error', role: 'alert', text: state.error }));

    var nav = h('div', { className: 'fs3-nav fs3-nav--sticky' });
    if (screen.nav && screen.nav.canGoBack) nav.appendChild(h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: 'Back', onClick: function () { move('back'); } }));
    if (screen.nav && screen.nav.canSkip) nav.appendChild(h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: 'Skip', onClick: function () { move('skip'); } }));
    // Continue is on every screen, even single-choice ones that answer on tap:
    // after Back the earlier answer comes back pre-selected (absorb() restores
    // it into the draft) and she needs a way to keep it without re-tapping.
    var ok = multi
      ? state.draft.length >= Math.max(1, sel.min || 0) && (!sel.max || state.draft.length <= sel.max)
      : state.draft.length === 1;
    nav.appendChild(primaryButton((screen.nav && screen.nav.nextLabel) || 'Continue', !ok || state.pending, function () { submitAnswer(state.draft.slice()); }));
    card.appendChild(nav);
    return card;
  }

  // The gradient "Continue →" pill every quiz screen ends with. The arrow is
  // Figma's f7:arrow-left flipped, drawn inline so it takes the button's colour.
  function primaryButton(label, disabled, onClick) {
    var btn = h('button', { className: 'fs3-btn fs3-btn--primary', type: 'button', disabled: disabled, onClick: onClick }, h('span', { text: label }));
    btn.insertAdjacentHTML('beforeend', '<svg class="fs3-btn__arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12h16M13 5l7 7-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    return btn;
  }

  function renderInterstitial(screen) {
    return h('div', { className: 'fs3-card fs3-quote' + (state.pending ? ' is-pending' : '') },
      h('p', { className: 'fs3-quote__text' }, rich(screen.copy)),
      h('div', { className: 'fs3-nav' },
        screen.nav && screen.nav.canGoBack ? h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: 'Back', onClick: function () { move('back'); } }) : null,
        // The label comes from the content (dashboard-editable), like every other screen's.
        primaryButton((screen.nav && screen.nav.nextLabel) || 'Continue', false, function () { move('continue'); })));
  }

  var buildingTimer = null;
  function renderBuilding(screen) {
    var list = h('ul', { className: 'fs3-checklist' });
    var steps = screen.steps || [];
    steps.forEach(function (s) { list.appendChild(h('li', { text: s })); });
    if (!buildingTimer) {
      var i = 0; var each = Math.max(700, (screen.minDurationMs || 4000) / (steps.length + 1));
      buildingTimer = setInterval(function () {
        var items = root.querySelectorAll('.fs3-checklist li');
        if (items[i]) items[i].classList.add('is-done');
        i += 1;
        if (i > steps.length) { clearInterval(buildingTimer); buildingTimer = null; move('continue'); }
      }, each);
    }
    return h('div', { className: 'fs3-card fs3-building' }, h('div', { className: 'fs3-spinner', 'aria-hidden': 'true' }),
      h('h2', { className: 'fs3-q', text: screen.title || 'Building your plan…' }), h('p', { className: 'fs3-help', text: screen.subtitle || '' }), list);
  }

  // The "your result is ready" gate, built to the Figma "Blur score" frame
  // (node 1469-15213 → 1480-44 desktop / 1466-15126 phone): a blurred gauge
  // preview plus three highlight rows (pattern, plan, and a locked/blurred
  // score row) and a dashed "10% off" callout, above Back / See my full
  // FabScore. Every string comes from the backend (`screen.copy`,
  // `screen.drivers` — fabscoreV3ScreenService in RtoManagement): the labels
  // from the pack's teaser copy, `copy.pattern` from her primary driver's
  // tagline ("Androgen driven"), `copy.plan` from the diet plan's cooking
  // style ("Home-cooked"), the coupon lines only while the coupon is on. Each
  // row/callout simply does not render until its own field is filled in,
  // same as the rest of this file treats missing dashboard copy; the label
  // fallbacks here are the Figma wording, for a pack that predates them.
  function renderTeaser(screen) {
    var copy = screen.copy || {};
    var R = window.FS3ResultArt || {};
    var art = R.teaser || {};
    var top = (screen.drivers || [])[0];

    var row = function (iconKey, tone, label, value, locked) {
      if (!cleanCopy(value)) return null;
      var ic = h('span', { className: 'fs3-ts__row-ic', style: tone ? 'color:' + tone : '', 'aria-hidden': 'true' });
      artInto(ic, art[iconKey]);
      var body = h('span', { className: 'fs3-ts__row-body' },
        h('span', { className: 'fs3-ts__row-label', text: label }),
        h('span', { className: 'fs3-ts__row-value', text: value }));
      return h('div', { className: 'fs3-ts__row' + (locked ? ' fs3-ts__row--locked' : '') }, ic, body);
    };

    var patternValue = cleanCopy(copy.pattern) || (top ? top.label : '');
    var rows = [
      row('pattern', '#e42c8b', copy.patternLabel || 'Your PCOS pattern', patternValue, false),
      row('plan', '#e42c8b', copy.planLabel || 'Your diet plan', copy.plan, false),
      row('lock', '#e42c8b', copy.lockedLabel || 'Your FabScore & full plan', patternValue, true),
    ].filter(Boolean);

    var promo = null;
    if (cleanCopy(copy.discountTitle)) {
      var promoIc = h('span', { className: 'fs3-ts__promo-ic', 'aria-hidden': 'true' }); artInto(promoIc, art.gift);
      promo = h('div', { className: 'fs3-ts__promo' }, promoIc,
        h('span', { className: 'fs3-ts__row-body' },
          h('span', { className: 'fs3-ts__promo-title', text: copy.discountTitle }),
          cleanCopy(copy.discountBody) ? h('span', { className: 'fs3-ts__promo-sub', text: copy.discountBody }) : null));
    }

    var gaugeArt = h('span', { className: 'fs3-ts__gauge-art', 'aria-hidden': 'true' });
    if (R.gauge) gaugeArt.innerHTML = R.gauge(72, 100, { locked: true });
    var gauge = h('div', { className: 'fs3-ts__gauge' },
      h('span', { className: 'fs3-ts__gauge-sr', text: 'Your FabScore (locked)' }),
      gaugeArt,
      h('span', { className: 'fs3-ts__gauge-label', 'aria-hidden': 'true', text: 'FABSCORE' }));

    var actions = h('div', { className: 'fs3-ts__actions' });
    if (screen.nav && screen.nav.canGoBack) actions.appendChild(h('button', { className: 'fs3-ts__back', type: 'button', text: 'Back', onClick: function () { move('back'); } }));
    var cta = h('button', { className: 'fs3-ts__cta', type: 'button', onClick: function () { move('continue'); } });
    cta.appendChild(document.createTextNode(copy.cta || 'See my full FabScore'));
    artInto(cta, (window.FS3LandingArt || {}).arrow);
    actions.appendChild(cta);

    var rowsEl = null;
    if (rows.length) { rowsEl = h('div', { className: 'fs3-ts__rows' }); rows.forEach(function (r) { rowsEl.appendChild(r); }); }

    return h('div', { className: 'fs3-ts' },
      h('div', { className: 'fs3-ts__head' },
        h('p', { className: 'fs3-ts__eyebrow', text: copy.eyebrow || 'Your FabScore is ready' }),
        h('h2', { className: 'fs3-ts__title', text: copy.title || 'Here’s the shape of it.' })),
      gauge,
      rowsEl,
      promo,
      actions);
  }

  function renderGate(screen) {
    var copy = screen.copy || {};
    var card = h('div', { className: 'fs3-card' }, h('h2', { className: 'fs3-q', text: copy.title || 'Where should we send your results?' }));
    loginForm(card, { from: 'gate', askProfile: true, nameLabel: copy.nameLabel, phoneLabel: copy.phoneLabel, cta: copy.cta || 'See my results', otpTitle: copy.otpTitle });
    return card;
  }

  function renderReview(screen) {
    track('review_viewed', { needs_answer_count: screen.sections.reduce(function (n, s) { return n + s.items.filter(function (i) { return i.status !== 'answered'; }).length; }, 0) });
    var card = h('div', { className: 'fs3-card' }, h('p', { className: 'fs3-eyebrow', text: 'Edit my answers' }), h('h2', { className: 'fs3-q', text: screen.title }), h('p', { className: 'fs3-help', text: screen.help }));
    screen.sections.forEach(function (section) {
      var block = h('div', { className: 'fs3-review__section' }, h('p', { className: 'fs3-group__label', text: section.label }));
      section.items.forEach(function (item) {
        block.appendChild(h('div', { className: 'fs3-review__row' },
          h('div', {}, h('p', { className: 'fs3-review__q' }, rich(item.title)),
            item.status === 'answered' ? h('p', { className: 'fs3-review__a', text: item.answerLabels.join(', ') }) : h('p', { className: 'fs3-review__a fs3-review__need', text: 'Needs an answer' })),
          h('button', { className: 'fs3-link', type: 'button', text: item.status === 'answered' ? 'Edit' : 'Answer', onClick: function () { move('jump', item.screenId); } })));
      });
      card.appendChild(block);
    });
    if (state.error) card.appendChild(h('div', { className: 'fs3-inline-error', role: 'alert', text: state.error }));
    card.appendChild(h('div', { className: 'fs3-nav' }, h('button', { className: 'fs3-btn fs3-btn--primary fs3-btn--block', type: 'button', disabled: !screen.canSubmit || state.pending, text: screen.submitLabel, onClick: function () {
      state.pending = true; render();
      api('POST', '/attempts/' + state.attempt.id + '/submit-edit', { clientSeq: nextSeq() }).then(function (res) {
        state.pending = false;
        if (!res.ok) { if (res.error.details && res.error.details.screen) { state.screen = res.error.details.screen; state.draft = []; } state.error = res.error.message; return render(); }
        if (res.data.outcome === 'no_changes') toast('Nothing changed, so your score is the same.');
        clearJourney();
        openResult(res.data.resultAttemptId);
      });
    } })));
    return card;
  }

  function renderQuiz() {
    var screen = state.screen;
    var progress = state.progress || { percent: 0, sections: [] };
    var body;
    if (!screen) body = h('div', { className: 'fs3-boot' }, h('div', { className: 'fs3-spinner' }));
    else if (screen.type === 'interstitial') body = renderInterstitial(screen);
    else if (screen.type === 'building') body = renderBuilding(screen);
    else if (screen.type === 'teaser') body = renderTeaser(screen);
    else if (screen.type === 'gate') body = renderGate(screen);
    else if (screen.type === 'date_of_birth') body = renderDateOfBirth(screen);
    else if (screen.type === 'date_and_select') body = renderDateAndSelect(screen);
    else if (screen.type === 'review') body = renderReview(screen);
    else body = renderQuestion(screen);

    // The teaser is the one quiz screen Figma draws without the quiz chrome
    // ("Blur score", 1466-15126 phone / 1480-43 desktop): a plain white bar
    // with the logo, then the white card on the pink ground — no rail, no
    // progress bar, no account menu. She is past the questions here, so the
    // step progress the rail carries has nothing left to say.
    if (screen && screen.type === 'teaser') {
      var images = {};
      try { images = JSON.parse(root.getAttribute('data-landing-images') || '{}'); } catch (e) { images = {}; }
      var brand = images.logo
        ? h('img', { className: 'fs3-ts-bar__logo', src: images.logo, alt: 'HealthFab', width: 194, height: 32 })
        : h('div', { className: 'fs3-wm' }, 'Health', h('b', { text: 'fab' }));
      return h('div', { className: 'fs3-app fs3-app--teaser' },
        h('header', { className: 'fs3-ts-bar' }, brand),
        h('div', { className: 'fs3-stage fs3-stage--teaser' }, body));
    }

    // Figma "Questions" frames — 1509-2191 (1440 wide) and 1509-2289 (360 wide).
    // Desktop: a 340px rail (logo, numbered stepper, the meta line pinned to the
    // bottom over a soft pink blob) beside the card. Phone: a sticky white bar
    // with the logo + Login, then the section name and an 11px progress bar
    // above the card. Both trees are always rendered; fabscore-v3.css shows the
    // rail from 1024px and the bar + progress below it.
    var images = {};
    try { images = JSON.parse(root.getAttribute('data-landing-images') || '{}'); } catch (e) { images = {}; }
    function logo(cls, w, hgt) {
      return images.logo
        ? h('img', { className: cls, src: images.logo, alt: 'HealthFab', width: w, height: hgt })
        : h('div', { className: 'fs3-wm' }, 'Health', h('b', { text: 'fab' }));
    }
    var sections = progress.sections || [];
    var stepper = h('ol', { className: 'fs3-stepper' });
    sections.forEach(function (s, i) {
      var dot = h('span', { className: 'fs3-step__dot', text: s.state === 'done' ? '' : String(i + 1) });
      if (s.state === 'done') dot.innerHTML = '<svg viewBox="0 0 12 10" fill="none" aria-hidden="true"><path d="M1.5 5.2 4.4 8 10.5 1.8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      stepper.appendChild(h('li', { className: 'fs3-step', 'data-state': s.state }, dot, h('span', { className: 'fs3-step__label', text: s.label })));
    });
    var current = sections.filter(function (s) { return s.state === 'current'; })[0];
    var sectionLabel = (screen && screen.eyebrow) || (current && current.label) || '';

    var shield = '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 1 2 2.5v3c0 2.6 1.7 4.4 4 5.5 2.3-1.1 4-2.9 4-5.5v-3L6 1z" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/><path d="m4.3 6 1.2 1.2L7.9 4.8" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var metaNote = h('p', { className: 'fs3-rail__note' });
    metaNote.innerHTML = shield;
    metaNote.appendChild(h('span', { text: 'Shaped with PCOS-aware gynaecologists & dietitians. Your answers stay private and are never sold.' }));

    return h('div', { className: 'fs3-app' },
      h('aside', { className: 'fs3-rail' },
        h('div', { className: 'fs3-rail__top' }, logo('fs3-rail__logo', 170, 30), topbar()),
        stepper,
        h('div', { className: 'fs3-rail__meta' }, progress.label ? h('p', { className: 'fs3-rail__label', text: progress.label }) : null, metaNote)),
      h('div', { className: 'fs3-stage' },
        // Figma's bar: logo + "Login" pill as one group, 20px apart, ending 16px
        // from the right edge (on the 360 frame that lands the logo dead centre).
        h('header', { className: 'fs3-qbar' }, logo('fs3-qbar__logo', 128, 21), topbar()),
        h('div', { className: 'fs3-qhead' },
          sectionLabel ? h('p', { className: 'fs3-qhead__label', text: sectionLabel }) : null,
          h('div', { className: 'fs3-progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(progress.percent || 0) }, h('div', { className: 'fs3-progress__bar', style: 'width:' + (progress.percent || 0) + '%' }))),
        body));
  }

  function gauge(score, min, max) {
    var t = Math.max(0, Math.min(1, (score - min) / ((max - min) || 1)));
    var r = 110; var cx = 132; var cy = 132; var len = Math.PI * r;
    var svg = '<svg viewBox="0 0 264 150" role="img" aria-label="FabScore ' + score + ' out of ' + max + '"><defs><linearGradient id="fs3g" x1="0" x2="1"><stop offset="0" stop-color="#ff63a9"/><stop offset="1" stop-color="#0c2b82"/></linearGradient></defs>'
      + '<path d="M ' + (cx - r) + ' ' + cy + ' A ' + r + ' ' + r + ' 0 0 1 ' + (cx + r) + ' ' + cy + '" fill="none" stroke="#ecdfe9" stroke-width="20" stroke-linecap="round"/>'
      + '<path d="M ' + (cx - r) + ' ' + cy + ' A ' + r + ' ' + r + ' 0 0 1 ' + (cx + r) + ' ' + cy + '" fill="none" stroke="url(#fs3g)" stroke-width="20" stroke-linecap="round" stroke-dasharray="' + (len * t) + ' ' + len + '"/></svg>';
    var holder = h('div'); holder.innerHTML = svg; return holder.firstChild;
  }

  // ── driver detail ─────────────────────────────────────────────────
  // Every word on this page comes from the backend (`driver.page`, the matched
  // `reasons`, and the labels in `copy.result`). Only the line icons live here.
  var DRIVER_ICONS = {
    metabolic: '<path d="M12 3c3 4 6 7.5 6 11a6 6 0 11-12 0c0-3.5 3-7 6-11z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
    androgen: '<circle cx="9" cy="9" r="5" stroke="currentColor" stroke-width="1.8"/><path d="M13 13l7 7M20 20v-5M20 20h-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
    inflammation: '<path d="M12 3c1.5 3 4 4.5 4 8a4 4 0 11-8 0c0-1 .3-1.8.8-2.6C9.3 9.6 10 11 10 11s.3-4.5 2-8z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
    stress: '<path d="M13 3L5 14h5l-1 7 8-11h-5l1-7z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"/>',
    back: '<path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    check: '<path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>',
    plan: '<path d="M6 4h9l3 3v13H6z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M9 12h6M9 16h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>'
  };
  // The four symptom tiles on a driver's page. The dashboard can reword a
  // symptom at any time, so a tile is matched on what the words SAY, never on
  // its position in the list: first keyword hit wins, and a symptom that hits
  // nothing keeps the driver's line icon — which is all this page drew before.
  var SYMPTOM_ART = {
    metabolic: [
      [['craving', 'sugar', 'carb', 'sweet'], 'cravings'],
      [['energy', 'crash', 'sleepy', 'slump'], 'energy'],
      [['weight', 'midsection', 'belly', 'tummy'], 'weight'],
      [['period', 'cycle', 'irregular'], 'cycle'],
    ],
    androgen: [
      [['acne', 'breakout', 'pimple', 'spot'], 'acne'],
      [['facial', 'body hair', 'unwanted hair', 'chin hair'], 'hair'],
      [['thinning', 'hair fall', 'hair loss', 'crown', 'scalp'], 'thinning'],
      [['oily', 'greasy', 'shine'], 'oily'],
    ],
    inflammation: [
      [['bloat', 'digest', 'gut', 'stomach'], 'bloating'],
      [['fatigue', 'tired', 'exhaust', 'drain'], 'fatigue'],
      [['sleep', 'insomnia', 'awake'], 'sleep'],
      [['mood', 'anxiety', 'anxious', 'low '], 'mood'],
    ],
    stress: [
      [['stress', 'tense', 'overwhelm', 'pressure'], 'stress'],
      [['shift', 'night', 'rotating'], 'shift'],
      [['sleep', 'insomnia', 'awake'], 'sleep'],
      [['mood', 'anxiety', 'anxious', 'low '], 'mood'],
    ],
  };
  function symptomArt(driverId, text) {
    var rules = SYMPTOM_ART[driverId] || [];
    var t = ' ' + String(text || '').toLowerCase() + ' ';
    for (var i = 0; i < rules.length; i++) {
      for (var j = 0; j < rules[i][0].length; j++) {
        if (t.indexOf(rules[i][0][j]) >= 0) return ART['sym_' + driverId + '_' + rules[i][1]] || '';
      }
    }
    return '';
  }

  // A driver added later in the dashboard has no drawing yet: it gets the drop.
  function icon(name) {
    var holder = h('span', { className: 'fs3-ic', 'aria-hidden': 'true' });
    holder.innerHTML = '<svg viewBox="0 0 24 24" fill="none">' + (DRIVER_ICONS[name] || DRIVER_ICONS.metabolic) + '</svg>';
    return holder;
  }

  function openDriver(d) {
    track('driver_card_clicked', { driver_id: d.driverId, pct: d.pct });
    if (!d.page) return;                      // no detail written for this driver yet
    state.detailDriverId = d.driverId;
    pushHistory();
    render();
    window.scrollTo(0, 0);
  }
  function closeDriver() { state.detailDriverId = null; render(); window.scrollTo(0, 0); }

  function renderDriverDetail(r, d, copy) {
    var pageCopy = d.page;
    var reasons = (d.reasons || []).map(cleanCopy).filter(Boolean);
    var total = r.plan.items.filter(function (i) { return !state.removed[i.key]; }).length;

    var answers = h('div', { className: 'fs3-dd__answers' }, h('h3', { text: copy.driverAnswersTitle || 'Your answers point to this' }));
    if (reasons.length) { var ul = h('ul'); reasons.forEach(function (x) { ul.appendChild(h('li', { text: x })); }); answers.appendChild(ul); }
    else answers.appendChild(h('p', { text: copy.driverNoAnswers || 'Nothing in your answers points here right now.' }));

    var symptoms = h('div', { className: 'fs3-dd__symgrid' });
    (pageCopy.symptoms || []).forEach(function (text) {
      var src = symptomArt(d.driverId, text);
      var tile = h('span', { className: 'fs3-dd__art' + (src ? ' fs3-dd__art--photo' : '') });
      tile.appendChild(src ? h('img', { src: src, alt: '', loading: 'lazy', width: '240', height: '240' }) : icon(d.icon));
      symptoms.appendChild(h('div', { className: 'fs3-dd__sym' }, tile, h('span', { text: text })));
    });

    var help = h('ul');
    (pageCopy.help || []).forEach(function (text) { help.appendChild(h('li', {}, h('span', { className: 'fs3-dd__ck' }, icon('check')), h('span', { text: text }))); });

    // exact shades from the backend when it sends them; otherwise the CSS mixes its own
    var shades = (d.colorWash ? ';--fs3-dd-wash:' + d.colorWash : '') + (d.colorDeep ? ';--fs3-dd-deep:' + d.colorDeep : '');
    return h('div', { className: 'fs3-dd', style: '--fs3-driver:' + (d.color || '#ff63a9') + shades },
      h('div', { className: 'fs3-dd__card' },
        h('div', { className: 'fs3-dd__top' },
          h('button', { className: 'fs3-dd__back', type: 'button', 'aria-label': copy.driverBack || 'Back to results', onClick: function () { window.history.back(); } }, icon('back')),
          h('span', { className: 'fs3-dd__kicker', text: pageCopy.kicker || d.label }),
          d.highlighted ? h('span', { className: 'fs3-dd__pill' }, h('i'), copy.driverPrimaryBadge || 'Primary driver for you') : null),
        h('div', { className: 'fs3-dd__body' },
          h('div', { className: 'fs3-dd__left' },
            h('span', { className: 'fs3-dd__icon' }, icon(d.icon)),
            h('div', { className: 'fs3-dd__pct', text: d.pct + '%' }),
            h('h1', { className: 'fs3-dd__title', text: pageCopy.title || d.label }),
            pageCopy.description ? h('p', { className: 'fs3-dd__desc', text: pageCopy.description }) : null,
            answers),
          h('div', { className: 'fs3-dd__right' },
            (pageCopy.symptoms || []).length ? h('div', { className: 'fs3-dd__box' }, h('h3', { text: copy.driverSymptomsTitle || 'Common symptoms in this pattern' }), symptoms) : null,
            pageCopy.why ? h('div', { className: 'fs3-dd__box fs3-dd__box--why' }, h('h3', { text: copy.driverWhyTitle || 'Why it matters' }), h('p', { text: pageCopy.why })) : null,
            (pageCopy.help || []).length ? h('div', { className: 'fs3-dd__box fs3-dd__box--help' }, h('h3', { text: copy.driverHelpTitle || 'What can help' }), help) : null))),
      h('div', { className: 'fs3-sticky' }, h('div', { className: 'fs3-sticky__inner fs3-dd__cta' },
        h('span', { className: 'fs3-dd__ctaic' }, icon('plan')),
        h('span', { className: 'fs3-dd__ctatxt' }, h('b', { text: copy.foundationsTitle || 'Your everyday foundations' }), h('span', { text: copy.foundationsSubtitle || '' })),
        h('button', { className: 'fs3-btn fs3-btn--accent', type: 'button', disabled: state.pending || !total, text: state.pending ? 'Adding…' : (copy.foundationsCta || 'Add plan to cart'), onClick: function () { addPlanToCart(false); } }))));
  }

  // ── score page ────────────────────────────────────────────────────
  // The page follows the Figma "New Results page" frames (fabscore-v3-result.css
  // carries the measurements, fabscore-v3-result-art.js the gauge and icons).
  // Every word, number, product and price comes from the backend; the two
  // drawings beside the note and the logo come from the section
  // (`data-landing-images`). The cart is untouched — "Get my Health Plan"
  // calls the same addPlanToCart() as before.

  // How each driver card is painted, from the Figma frames. The backend's
  // `theme` wins when it sends one (so the dashboard can recolour a driver);
  // a driver the file never drew is mixed from its plain `color`.
  var RESULT_PALETTE = {
    metabolic:    { color: '#ffa100', deep: '#e39300', tint: '#fdf1d6', ring: '#f19b01', pillBg: '#fffbf1', pillBd: '#f3eaa1', pillFg: '#a25300' },
    androgen:     { color: '#008cd7', deep: '#005be3', tint: '#e9f1fd', ring: '#008cd7', pillBg: '#ffffff', pillBd: '#cce8f7', pillFg: '#008cd7', icon: '#0087cf' },
    inflammation: { color: '#ff5500', deep: '#e33900', tint: '#ffe6da', ring: '#ff5500', pillBg: '#fff8f4', pillBd: '#ffd2cb', pillFg: '#ff5500' },
    stress:       { color: '#b100ef', deep: '#a000d7', tint: '#eee7fe', ring: '#b100ef', pillBg: '#ffffff', pillBd: '#f7e2ff', pillFg: '#b100ef' },
  };
  function driverTheme(d) {
    if (d.theme && d.theme.color) return d.theme;
    if (RESULT_PALETTE[d.driverId]) return RESULT_PALETTE[d.driverId];
    var c = d.color || '#ff63a9';
    return { color: c, deep: d.colorDeep || 'color-mix(in srgb, ' + c + ' 80%, #000)', tint: d.colorWash || 'color-mix(in srgb, ' + c + ' 14%, #fff)', ring: c,
      pillBg: '#fff', pillBd: 'color-mix(in srgb, ' + c + ' 25%, #fff)', pillFg: c };
  }
  // The status pill. The Figma frame labels the four cards by their order —
  // Main pattern, Focus Area, Present, Less Prominent — so it follows the
  // backend's rank (1–4); the backend's own `status` wins when it sends one,
  // and the words come from `copy.result.driverStatus` when the dashboard has them.
  var STATUS_DEFAULT = { primary: 'Main pattern', second: 'Focus Area', third: 'Present', rest: 'Less Prominent' };
  function driverStatus(d, copy, index) {
    if (d.status) return d.status;
    var words = copy.driverStatus || {};
    var rank = Number(d.rank) || (index + 1);
    var key = d.highlighted ? 'primary' : rank <= 2 ? 'second' : rank === 3 ? 'third' : 'rest';
    return words[key] || STATUS_DEFAULT[key];
  }
  // "Cycle: 45+ days" + "Cycle: 32 days" → "Cycle: 45+ days → 32 days"
  function reviewMetric(t) {
    if (t.metric) return t.metric;
    var before = String(t.before || ''), after = String(t.after || '');
    var m = before.match(/^([^:]+:\s*)/);
    if (m && after.indexOf(m[1]) === 0) after = after.slice(m[1].length);
    return before && after ? before + ' → ' + after : (before || after);
  }
  // The note under her score — two states, as the Figma frame draws them: a
  // score above 85 gets "You are in a healthy range" and her drawing, every
  // other score gets "Worth getting checked" and the clinician. The backend's
  // `verdict` decides when it sends one; until then the band id decides.
  function resultVerdict(r, copy) {
    if (r.verdict && (r.verdict.title || r.verdict.text)) return r.verdict;
    var band = (r.fabscore && r.fabscore.band && r.fabscore.band.id) || '';
    if (r.healthy || band === 'best' || band === 'very_low' || band === 'low') {
      return { tone: 'healthy', title: copy.healthyTitle || 'You are in a healthy range', text: copy.healthyText || 'Your current symptoms do not suggest PCOS. Keep up the good habits & stay consistent.' };
    }
    return { tone: 'warn', title: copy.safetyTitle || 'Worth getting checked', text: (r.safety && r.safety.message) || copy.safetyMessage || 'Consider speaking with a gynaecologist or endocrinologist for a proper evaluation.' };
  }

  function renderResult() {
    var r = state.result;
    var R = window.FS3ResultArt;
    var copy = (r && r.copy && r.copy.result) || {};
    if (!r || !R || !R.gauge || !copy.snapshotTitle) return renderResultBasic();     // old content, or art not loaded
    var open = state.detailDriverId && r.drivers.filter(function (d) { return d.driverId === state.detailDriverId; })[0];
    if (open && open.page) return renderDriverDetail(r, open, copy);

    var images = {};
    try { images = JSON.parse(root.getAttribute('data-landing-images') || '{}'); } catch (e) { images = {}; }
    var fill = function (text, values) { return String(text || '').replace(/\{(\w+)\}/g, function (m, k) { return values[k] === undefined || values[k] === null ? '' : String(values[k]); }); };
    // "Once claimed, your offer is valid for only 4 hours" — "only" and "4 hours" in bold, as in Figma.
    var claimNote = function (template, hours) {
      var parts = String(template || 'Once claimed, your offer is valid for only {hours} hours').split('{hours}');
      var note = h('span', { className: 'rs-offer__note-text' });
      var lead = parts[0] || '';
      var only = /only\s*$/.test(lead) ? lead.replace(/only\s*$/, '') : null;
      note.appendChild(document.createTextNode(only === null ? lead : only));
      if (only !== null) { note.appendChild(h('b', { text: 'only' })); note.appendChild(document.createTextNode(' ')); }
      note.appendChild(h('b', { text: String(hours) + (parts[1] || '') }));
      return note;
    };
    // The claim-offer box. Before her tap: headline, "Claim offer", the hours note.
    // After: the dark "Offer ends in 03:59:59" tab, headline, her code and Copy.
    var claimBox = function (c, saving) {
      var live = !!(c.code && c.expiresAt);
      var title = saving > 0
        ? fill(copy.planSavingsCoupon || 'Save {amount} + extra {percent}% off', { amount: money(saving), percent: c.percentage })
        : fill(copy.planCouponOnly || 'Extra {percent}% off', { percent: c.percentage });
      var box = h('div', { className: 'rs-offer' + (live ? ' is-live' : '') });
      if (live) {
        box.appendChild(h('div', { className: 'rs-offer__timer', role: 'timer' },
          h('img', { className: 'rs-offer__clock', src: images.offerClockLight || '', alt: '', width: 14, height: 14 }),
          h('span', { text: (copy.offerEndsIn || 'Offer ends in') + ' ' }),
          h('b', { 'data-fs3-countdown': '', text: timeLeft(msLeft(c)) })));
      }
      var card = h('div', { className: 'rs-offer__card' },
        h('div', { className: 'rs-offer__title' }, ic('tag', 'rs-offer__ic'), h('strong', { text: title })));
      if (live) {
        card.appendChild(h('div', { className: 'rs-offer__code-row' },
          h('span', { className: 'rs-offer__code' }, (copy.codeLabel || 'Code:') + ' ', h('b', { text: c.code })),
          h('button', { className: 'rs-offer__copy', type: 'button', text: copy.copyLabel || 'Copy', 'aria-label': 'Copy code ' + c.code, onClick: function () { if (navigator.clipboard) navigator.clipboard.writeText(c.code); track('coupon_copied'); toast('Code copied'); } })));
      } else {
        var busy = state.claiming || c.status === 'creating';
        var claim = h('button', { className: 'rs-offer__claim', type: 'button', disabled: busy, onClick: claimOffer });
        claim.appendChild(h('span', { text: busy ? (copy.claimingLabel || 'Claiming…') : (copy.claimCta || 'Claim offer') }));
        if (!busy) artInto(claim, R.arrow);
        card.appendChild(claim);
        if (c.lastClaimFailed && !busy) card.appendChild(h('span', { className: 'rs-offer__error', role: 'alert', text: copy.claimFailed || 'Couldn’t claim your offer — tap to try again' }));
        card.appendChild(h('span', { className: 'rs-offer__note' },
          h('img', { className: 'rs-offer__clock', src: images.offerClock || '', alt: '', width: 12, height: 12 }),
          claimNote(copy.claimNote, c.validHours || 4)));
      }
      box.appendChild(card);
      return box;
    };
    var inner = function (cls, children) {
      var el = h('div', { className: 'rs-in' + (cls ? ' ' + cls : '') });
      children.forEach(function (child) { if (child) el.appendChild(child); });
      return el;
    };
    var head = function (eyebrow, title, sub) {
      var el = h('div', { className: 'rs-head' });
      if (eyebrow) el.appendChild(lpText('div', 'rs-eyebrow', eyebrow));
      if (title) el.appendChild(lpText('h2', 'rs-h2', title));
      if (sub) el.appendChild(lpText('p', 'rs-sub', sub));
      return el;
    };
    var ic = function (name, cls) { var s = h('span', { className: cls || '', 'aria-hidden': 'true' }); artInto(s, R[name]); return s; };

    // header — the logo, her account (or Log in) and the cart
    var logo = h('a', { className: 'rs-logo', href: '/', 'aria-label': 'HealthFab' },
      images.logo ? h('img', { src: images.logo, alt: 'HealthFab', width: 194, height: 32 }) : h('span', { className: 'rs-logo__text' }, 'Health', h('b', { text: 'fab' })));
    var header = h('header', { className: 'rs-header' }, h('div', { className: 'rs-header__in' }, logo, h('div', { className: 'rs-header__right' }, topbar())));

    // score card — "Here's your Health FabScore, Ananya♡", the gauge, the note
    // A line break in the copy ("Here’s your\nHealth FabScore") is kept, as the Figma frame breaks it.
    var title = h('h1', { className: 'rs-score__title' });
    String(copy.snapshotTitle || '').split(/\r?\n/).forEach(function (line, i) { if (i) title.appendChild(h('br')); title.appendChild(document.createTextNode(line)); });
    if (r.customer.firstName) title.appendChild(document.createTextNode(', ' + r.customer.firstName));
    title.appendChild(h('span', { className: 'rs-heart', 'aria-hidden': 'true', text: '♡' }));
    var gauge = h('div', { className: 'rs-gauge' }); gauge.innerHTML = R.gauge(r.fabscore.score, r.fabscore.max);
    var verdict = resultVerdict(r, copy);
    // The line under the gauge is worded by the verdict, as the two Figma frames
    // word it differently; an older pack sends none and the one caption is used.
    var gaugeCol = h('div', { className: 'rs-score__gauge' }, gauge, lpText('p', 'rs-score__cap', verdict.caption || copy.snapshotCaption));
    if (r.attempt.kind === 'edit' && r.attempt.parentFabscore !== null && r.attempt.parentFabscore !== undefined) {
      gaugeCol.appendChild(h('p', { className: 'rs-score__delta', text: fill(copy.updatedFrom || 'Updated from {before} to {after}', { before: r.attempt.parentFabscore, after: r.fabscore.score }) }));
    }
    var banner = function (tone, titleText, bodyHtml) {
      var art = tone === 'healthy' ? (images.noteHealthy || ART.note_reassure) : tone === 'warn' ? (images.noteDoctor || ART.note_doctor) : (images.noteHealthy || ART.note_reassure);
      var box = h('div', { className: 'rs-banner rs-banner--' + tone });
      if (art) box.appendChild(h('img', { className: 'rs-banner__art', src: art, alt: '', loading: 'lazy' }));
      var txt = h('span', { className: 'rs-banner__txt' });
      if (titleText) txt.appendChild(h('strong', { className: 'rs-banner__title', text: titleText }));
      txt.appendChild(richInto(h('span', { className: 'rs-banner__body' }), bodyHtml || ''));
      box.appendChild(txt);
      return box;
    };
    var card = h('div', { className: 'rs-score__card' }, title, gaugeCol);
    // One banner, because the card gives them a single slot. The pregnancy note
    // is the one she has to read, so it takes the slot whenever she has one.
    if (r.pregnancyNote) card.appendChild(banner('neutral', r.pregnancyNote.title, r.pregnancyNote.text));
    else card.appendChild(banner(verdict.tone || 'warn', verdict.title, verdict.text));
    var score = h('section', { className: 'rs-score', id: 'fs3-score' }, inner('', [card]));

    // what's driving your score — four cards, her top one ringed
    var anyDriver = r.drivers.some(function (d) { return d.highlighted; });
    var grid = h('div', { className: 'rs-drivers__grid' });
    r.drivers.forEach(function (d, index) {
      var t = driverTheme(d);
      var style = '--d:' + t.color + ';--d2:' + (t.deep || t.color) + ';--di:' + (t.icon || t.color) + ';--tint:' + (t.tint || '#fff') + ';--ring:' + (t.ring || t.color) +
        ';--pill-bg:' + (t.pillBg || '#fff') + ';--pill-bd:' + (t.pillBd || t.color) + ';--pill-fg:' + (t.pillFg || t.color);
      var tagline = cleanCopy(d.tagline || d.summary);
      // Each driver has its own Figma glyph (drop / magnifier / flame / bolt); a
      // driver added in the dashboard wears the drop until a drawing is made.
      var badge = h('span', { className: 'rs-driver__ic', 'aria-hidden': 'true' });
      badge.innerHTML = R.driverIcon ? R.driverIcon(d.driverId || d.icon, d.driverId || index) : (R.drop || '');
      var cardEl = h('button', { className: 'rs-driver' + (d.highlighted ? ' is-primary' : ''), type: 'button', style: style,
        'aria-label': d.label + ', ' + d.pct + '%. ' + (copy.driverReadMore || 'Read more'), onClick: function () { openDriver(d); } },
        h('span', { className: 'rs-driver__row' }, badge, h('span', { className: 'rs-driver__pill', text: driverStatus(d, copy, index) })),
        h('span', { className: 'rs-driver__name', text: d.label }),
        tagline ? h('span', { className: 'rs-driver__tag', text: tagline }) : null,
        h('span', { className: 'rs-driver__pct', text: d.pct + '%' }),
        h('span', { className: 'rs-driver__bar' }, h('i', { style: 'width:' + Math.max(0, Math.min(100, d.pct)) + '%' })),
        // What that percentage means, in a sentence. The words come from the
        // pack (scoring.drivers.levels), so they are edited there, not here.
        d.levelMeaning ? h('span', { className: 'rs-driver__mean', text: d.levelMeaning }) : null,
        h('span', { className: 'rs-driver__more' }, h('span', { text: copy.driverReadMore || 'Read more' })));
      grid.appendChild(cardEl);
    });
    var drivers = h('section', { className: 'rs-drivers', id: 'fs3-drivers' }, inner('', [
      head(copy.driversEyebrow, copy.driversHelp),
      anyDriver ? null : (copy.driversNone ? lpText('p', 'rs-drivers__none', copy.driversNone) : null),
      grid]));

    // your 30-day plan — live Shopify products and prices; size is the only choice this store sells by
    var c = r.coupon || {};
    var total = 0; var compare = 0;
    var list = h('div', { className: 'rs-plan__list' });
    r.plan.items.forEach(function (item) {
      // an included row (Movement Plan with the Diet Plan) follows its parent: no price, no button
      var off = item.included ? !!state.removed[item.includedWith] : !!state.removed[item.key];
      var variant = (item.variants || []).filter(function (v) { return v.variantId === (state.sizes[item.key] || item.variantId); })[0];
      var price = variant ? variant.price : item.price;
      var was = variant ? variant.compareAtPrice : item.compareAtPrice;
      var cheaper = was && was > price;
      if (!off && !item.included) { total += price || 0; compare += cheaper ? was : (price || 0); }

      var picture = planCover(item, images) || item.image;
      var pic = h('span', { className: 'rs-item__pic' });
      if (picture) pic.appendChild(h('img', { src: picture, alt: '', loading: 'lazy', width: 89, height: 89 }));
      else pic.innerHTML = (R.kit || {})[item.icon] || (R.kit || {}).book || '';
      var body = h('span', { className: 'rs-item__body' }, h('span', { className: 'rs-item__title', text: item.title }));
      var about = cleanCopy(item.description);
      if (about) body.appendChild(h('span', { className: 'rs-item__desc', text: about }));
      if (item.included) {
        var badge = h('span', { className: 'rs-item__included' }); artInto(badge, R.lock); badge.appendChild(document.createTextNode(copy.includedPrice || 'Included'));
        body.appendChild(h('span', { className: 'rs-item__price' }, badge));
      } else body.appendChild(h('span', { className: 'rs-item__price' }, h('span', { className: 'rs-item__now', text: money(price) }),
        cheaper ? h('span', { className: 'rs-item__was', text: money(was) }) : null,
        cheaper ? h('span', { className: 'rs-item__off', text: Math.round((1 - price / was) * 100) + '% OFF' }) : null));
      if (item.variantPicker && (item.variants || []).length > 1) {
        var chosen = variant || item.variants[0];
        var select = h('select', { 'aria-label': 'Choose size for ' + item.title, disabled: off, onChange: function (e) { state.sizes[item.key] = Number(e.target.value); track('size_changed', { product_key: item.key }); render(); } });
        item.variants.forEach(function (v) { select.appendChild(h('option', { value: String(v.variantId), selected: v.variantId === chosen.variantId, text: v.title })); });
        body.appendChild(h('label', { className: 'rs-item__size' }, 'Size:', h('b', { text: chosen.title }), ic('chevron'), select));
      }
      // The Diet Plan's own two pickers (Figma "Diet plan dropdown"). The
      // backend sends the labels and what she answered in the quiz; her change
      // here only rides along on the cart line as a line-item property, so it
      // decides which PDF is sent and never touches her answers or her score.
      if ((item.personalize || []).length) {
        var picks = h('span', { className: 'rs-item__picks' });
        item.personalize.forEach(function (picker) {
          var current = (state.picks[item.key] || {})[picker.id] || picker.selected;
          var opts = h('select', {
            'aria-label': 'Choose ' + picker.label.toLowerCase() + ' for ' + item.title, disabled: off,
            onChange: function (e) {
              var chosenPicks = state.picks[item.key] || (state.picks[item.key] = {});
              chosenPicks[picker.id] = e.target.value;
              track('plan_option_changed', { product_key: item.key, option: picker.id });
              render();
            },
          });
          picker.options.forEach(function (option) { opts.appendChild(h('option', { value: option, selected: option === current, text: option })); });
          picks.appendChild(h('label', { className: 'rs-item__size rs-item__pick' }, picker.label + ':', h('b', { text: current }), ic('chevron'), opts));
        });
        body.appendChild(picks);
      }
      // an included row rides on its parent: no bin of its own
      var toggle = null;
      if (!item.included) {
        toggle = h('button', { className: 'rs-item__remove', type: 'button', 'aria-label': (off ? 'Add back ' : 'Remove ') + item.title, onClick: function () {
          state.removed[item.key] = !off;
          track(off ? 'plan_item_added' : 'plan_item_removed', { product_key: item.key, price: price });
          render();
        } });
        if (off) toggle.textContent = '+'; else artInto(toggle, R.trash);
      }
      // The whole row opens the popup: one target, one label, and the markup
      // around it untouched. It sits BEHIND the row's own controls (CSS), so
      // the size select, the pickers and the bin still take their own taps.
      var open = h('button', {
        className: 'rs-item__open', type: 'button', 'aria-label': 'View details for ' + item.title,
        onClick: function () { openProductSheet(item, images); },
      });
      // Everything under the price comes from the product's own page, which is
      // a page-sized fetch. Starting it when she reaches for the row — hover on
      // a mouse, first touch on a phone — means it is usually already in hand
      // by the time the card opens, instead of a card that fills in late.
      var warm = function () { loadProductExtras(item.handle); };
      open.addEventListener('pointerenter', warm, { once: true });
      open.addEventListener('touchstart', warm, { once: true, passive: true });
      open.addEventListener('focus', warm, { once: true });
      var row = h('div', { className: 'rs-item' + (off ? ' is-off' : '') + (item.included ? ' rs-item--included' : ''), 'data-key': item.key }, open, pic, body, toggle);
      // An included row (Movement Plan with the Diet Plan) is drawn inside its parent's group, so the two read as one.
      var parent = item.included && item.includedWith ? list.querySelector('.rs-item-group[data-key="' + item.includedWith + '"]') : null;
      if (parent) { parent.classList.add('rs-item-group--bundle'); parent.appendChild(row); }
      else list.appendChild(h('div', { className: 'rs-item-group', 'data-key': item.key }, row));
    });
    var saving = compare > total ? compare - total : 0;
    var foot = h('div', { className: 'rs-plan__foot' },
      h('div', { className: 'rs-plan__sub' }, h('span', { className: 'rs-plan__sublab', text: copy.subtotalLabel || 'Subtotal' }),
        h('span', { className: 'rs-plan__amt' }, h('span', { className: 'rs-plan__now', text: money(total) }), saving > 0 ? h('span', { className: 'rs-plan__was', text: money(compare) }) : null)));
    // Claim mode: the "Claim offer" box (Figma 1932:29258), or nothing at all
    // once she has ordered (hidden) — then only "Get my plan" stays.
    if (c.mode === 'claim') {
      if (!c.hidden && c.status !== 'disabled') foot.appendChild(claimBox(c, saving));
    }
    // One offer box: "Save ₹600 + extra 10% off" with her code and a Copy button.
    // Without a code yet: the saving line alone (and the "creating" message under it).
    var offer = null;
    var headline = null;
    if (c.mode !== 'claim') {
    if (saving > 0 && c.code) headline = fill(copy.planSavingsCoupon || 'Save {amount} + extra {percent}% off', { amount: money(saving), percent: c.percentage });
    else if (saving > 0) headline = fill(copy.planSavings || 'Save {amount}', { amount: money(saving) });
    else if (c.code) headline = fill(copy.planCouponOnly || 'Extra {percent}% off', { percent: c.percentage });
    if (headline || c.message) {
      offer = h('div', { className: 'rs-plan__offer' }, ic('tag', 'rs-plan__offer-ic'), h('div', { className: 'rs-plan__offer-body' }));
      var offerBody = offer.lastChild;
      if (headline) offerBody.appendChild(h('strong', { className: 'rs-plan__offer-title', text: headline }));
      if (c.code) {
        offerBody.appendChild(h('div', { className: 'rs-plan__offer-code' },
          h('span', { className: 'rs-plan__code' }, (copy.codeLabel || 'Code:') + ' ', h('b', { text: c.code })),
          h('button', { className: 'rs-plan__copy', type: 'button', text: copy.copyLabel || 'Copy', 'aria-label': 'Copy code ' + c.code, onClick: function () { if (navigator.clipboard) navigator.clipboard.writeText(c.code); track('coupon_copied'); toast('Code copied'); } })));
      } else if (c.message) {
        offerBody.appendChild(h('span', { className: 'rs-plan__offer-note', text: fillPercent(c.message, c.percentage) }));
      }
      foot.appendChild(offer);
    }
    }
    var buy = h('button', { className: 'rs-cta', type: 'button', disabled: state.pending || !total, onClick: function () { addPlanToCart(false); } });
    buy.appendChild(document.createTextNode(state.pending ? 'Adding…' : fill(copy.planCta || 'Get my plan · {total}', { total: money(total) })));
    artInto(buy, R.arrow);
    foot.appendChild(buy);
    var planError = state.error ? h('div', { className: 'fs3-inline-error', role: 'alert', text: state.error }) : null;
    var plan = h('section', { className: 'rs-plan', id: 'fs3-bundle' }, inner('', [h('div', { className: 'rs-plan__card' },
      h('div', { className: 'rs-plan__head' }, head(copy.planEyebrow, copy.planTitle, copy.planHelp)), list, foot, planError)]));

    // real stories — from women with the same top driver
    var stories = null;
    if (r.reviews && (r.reviews.items || []).length) {
      var scroll = h('div', { className: 'rs-stories__scroll' });
      r.reviews.items.forEach(function (t) {
        var stars = h('div', { className: 'rs-story__stars', 'aria-label': '5 stars' });
        for (var i = 0; i < 5; i++) artInto(stars, R.star);
        // "Cycle: 45+ days → 32 days": the arrow is drawn (Montserrat has no arrow glyph, so text falls back to another face)
        var metric = reviewMetric(t);
        var chip = null;
        if (metric) {
          chip = h('span', { className: 'rs-story__chip' });
          var parts = metric.split(' → ');
          parts.forEach(function (part, pi) { if (pi) artInto(chip, R.arrow); chip.appendChild(document.createTextNode(part)); });
        }
        scroll.appendChild(h('div', { className: 'rs-story' }, stars, h('p', { className: 'rs-story__quote', text: t.quote }),
          h('span', { className: 'rs-story__name', text: t.name }), chip));
      });
      stories = h('section', { className: 'rs-stories', id: 'fs3-stories' }, inner('', [
        // The Figma frame's heading is the same for every pattern; the words come from the
        // content pack, with the frame's own as the fallback until the new pack is published.
        head(r.reviews.eyebrow || copy.reviewsEyebrow || 'Real stories, real progress',
          r.reviews.title || copy.reviewsTitleNoDriver || 'Women like you, real results',
          r.reviews.subtitle || copy.reviewsSubtitle || 'Different journeys, similar patterns. Here’s what women with similar results have to say.'), scroll]));
    }

    // The Figma frame ends at the stories. Start over / edit answers live in the account menu.
    return h('div', { className: 'fs3-rs' }, header, score, drivers, plan, stories);
  }

  // The plain score page: used only while a shop's published content predates
  // the full page (no `copy.result.snapshotTitle`), so nothing breaks mid-rollout.
  function renderResultBasic() {
    var r = state.result;
    if (!r) return h('div', { className: 'fs3-boot' }, h('div', { className: 'fs3-spinner' }), h('p', { text: 'Loading your results…' }));
    var copy = (r.copy && r.copy.result) || {};
    var open = state.detailDriverId && r.drivers.filter(function (d) { return d.driverId === state.detailDriverId; })[0];
    if (open && open.page) return renderDriverDetail(r, open, copy);
    var page = h('div', { className: 'fs3-result' });

    page.appendChild(h('div', { className: 'fs3-result__top' }, h('div', { className: 'fs3-wm' }, 'Health', h('b', { text: 'fab' })), topbar()));
    page.appendChild(h('div', {}, h('p', { className: 'fs3-eyebrow', text: copy.eyebrow || 'Your FabScore results' }),
      h('h1', { style: 'font-size:clamp(26px,5vw,34px)', text: (r.customer.firstName ? r.customer.firstName + ', here’s' : 'Here’s') + ' what we found' })));

    var g = h('div', { className: 'fs3-panel fs3-gauge' }, gauge(r.fabscore.score, r.fabscore.min, r.fabscore.max),
      h('div', { className: 'fs3-gauge__score', text: String(r.fabscore.score) }), h('div', { className: 'fs3-gauge__label', text: 'Your FabScore · out of ' + r.fabscore.max }),
      r.fabscore.band ? h('span', { className: 'fs3-gauge__band', text: r.fabscore.band.label }) : null);
    if (r.attempt.kind === 'edit' && r.attempt.parentFabscore !== null) g.appendChild(h('p', { className: 'fs3-delta' + (r.fabscore.score < r.attempt.parentFabscore ? ' fs3-delta--down' : ''), text: 'Updated from ' + r.attempt.parentFabscore + ' → ' + r.fabscore.score }));
    page.appendChild(g);

    if (cleanCopy(r.connectTheDots)) page.appendChild(h('div', { className: 'fs3-panel' }, h('p', { className: 'fs3-eyebrow', text: 'What this means for you' }), h('p', { className: 'fs3-ctd' }, rich(r.connectTheDots))));
    if (r.safety && r.safety.message) page.appendChild(h('div', { className: 'fs3-panel', style: 'border-color:var(--fs3-warn)' }, h('p', { className: 'fs3-eyebrow', style: 'color:var(--fs3-warn)', text: 'Worth checking soon' }), h('p', { text: r.safety.message })));

    // Drivers — however many the backend sends, already in order.
    var drivers = h('div', { className: 'fs3-drivers' });
    r.drivers.forEach(function (d) {
      var card = h('button', { className: 'fs3-driver' + (d.highlighted ? ' is-top' : ''), type: 'button', style: '--fs3-driver:' + (d.color || '#ff63a9') + ';text-align:left', 'aria-label': d.label + ', ' + d.pct + '%. See details', onClick: function () { openDriver(d); } },
        h('div', { className: 'fs3-driver__head' }, h('span', { className: 'fs3-driver__name', text: d.label }), h('span', { className: 'fs3-driver__pct', text: d.pct + '%' })),
        h('div', { className: 'fs3-bar' }, h('i', { style: 'width:' + d.pct + '%' })));
      drivers.appendChild(card);
    });
    page.appendChild(h('div', { className: 'fs3-panel' }, h('h2', { text: copy.driversTitle || 'What’s driving it' }), drivers, h('p', { className: 'fs3-note', text: copy.driversNote || 'This is your reported pattern, not a diagnosis.' })));

    // Coupon
    var c = r.coupon || {};
    if (c.mode === 'claim') {
      // Claim mode, simple layout: the same three states as the main page.
      if (!c.hidden && c.status !== 'disabled') {
        var claimPanel = h('div', { className: 'fs3-panel fs3-coupon' }, h('h2', { text: fillPercent(copy.couponTitle || 'Your code', c.percentage) }));
        if (c.code && c.expiresAt) {
          claimPanel.appendChild(h('p', { className: 'fs3-muted' }, (copy.offerEndsIn || 'Offer ends in') + ' ', h('b', { 'data-fs3-countdown': '', text: timeLeft(msLeft(c)) })));
          claimPanel.appendChild(h('div', { className: 'fs3-coupon__row' }, h('span', { className: 'fs3-coupon__code', text: c.code }),
            h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: copy.copyLabel || 'Copy', onClick: function () { if (navigator.clipboard) navigator.clipboard.writeText(c.code); track('coupon_copied'); toast('Code copied'); } })));
        } else {
          var busyClaim = state.claiming || c.status === 'creating';
          claimPanel.appendChild(h('button', { className: 'fs3-btn', type: 'button', disabled: busyClaim, onClick: claimOffer, text: busyClaim ? (copy.claimingLabel || 'Claiming…') : (copy.claimCta || 'Claim offer') }));
          claimPanel.appendChild(h('p', { className: 'fs3-muted', text: String(copy.claimNote || 'Once claimed, your offer is valid for only {hours} hours').replace('{hours}', String(c.validHours || 4)) }));
        }
        page.appendChild(claimPanel);
      }
    } else if (c.code) {
      page.appendChild(h('div', { className: 'fs3-panel fs3-coupon' }, h('h2', { text: fillPercent(copy.couponTitle || 'Your code', c.percentage) }),
        h('div', { className: 'fs3-coupon__row' }, h('span', { className: 'fs3-coupon__code', text: c.code }),
          h('button', { className: 'fs3-btn fs3-btn--ghost', type: 'button', text: 'Copy', onClick: function () { if (navigator.clipboard) navigator.clipboard.writeText(c.code); track('coupon_copied'); toast('Code copied'); } }),
          state.couponApplied ? h('span', { className: 'fs3-tag', text: 'Applied to your bag' }) : null),
        h('p', { className: 'fs3-muted', text: 'It is added automatically when you add your plan to the cart.' })));
    } else if (c.message) page.appendChild(h('div', { className: 'fs3-panel fs3-coupon' }, h('p', { text: fillPercent(c.message, c.percentage) })));

    // Plan
    var plan = h('div', { className: 'fs3-plan' });
    var total = 0; var compare = 0;
    r.plan.items.forEach(function (item) {
      var off = !!state.removed[item.key];
      var variant = (item.variants || []).filter(function (v) { return v.variantId === (state.sizes[item.key] || item.variantId); })[0];
      var price = variant ? variant.price : item.price;
      var was = variant ? variant.compareAtPrice : item.compareAtPrice;
      if (!off) { total += price || 0; compare += was || price || 0; }

      var info = h('div', {}, h('p', { className: 'fs3-item__title', text: item.title }), cleanCopy(item.reason) ? h('p', { className: 'fs3-item__reason', text: item.reason }) : null);
      if (item.variantPicker && (item.variants || []).length > 1) {
        var select = h('select', { 'aria-label': 'Choose your size for ' + item.title, onChange: function (e) { state.sizes[item.key] = Number(e.target.value); track('size_changed', { product_key: item.key }); render(); } });
        item.variants.forEach(function (v) { select.appendChild(h('option', { value: String(v.variantId), selected: v.variantId === (state.sizes[item.key] || item.variantId), text: v.title })); });
        info.appendChild(select);
      }
      var row = h('div', { className: 'fs3-item' + (off ? ' is-off' : '') },
        item.image ? h('img', { className: 'fs3-item__img', src: item.image, alt: '', loading: 'lazy', width: '64', height: '64' }) : h('div', { className: 'fs3-item__img' }),
        info,
        h('div', { className: 'fs3-item__price' }, was && was > price ? h('s', { text: money(was) }) : null, money(price)),
        h('button', { className: 'fs3-item__toggle', type: 'button', text: off ? 'Add back' : 'Remove', onClick: function () {
          state.removed[item.key] = !off;
          track(off ? 'plan_item_added' : 'plan_item_removed', { product_key: item.key, price: price });
          render();
        } }));
      plan.appendChild(row);
    });
    page.appendChild(h('div', { className: 'fs3-panel' }, h('h2', { text: copy.planTitle || 'Your plan' }), plan,
      h('div', { className: 'fs3-total' }, h('span', { text: 'Total' + (c.code && c.percentage ? ' before your ' + c.percentage + '% code' : '') }), h('b', { text: money(total) }))));

    if (cleanCopy(copy.doctorNote)) page.appendChild(h('p', { className: 'fs3-note', style: 'text-align:center', text: copy.doctorNote }));

    page.appendChild(h('div', { className: 'fs3-sticky' }, h('div', { className: 'fs3-sticky__inner' },
      h('button', { className: 'fs3-btn fs3-btn--accent', type: 'button', disabled: state.pending || !r.plan.items.length, text: state.pending ? 'Adding…' : 'Add my plan to cart · ' + money(total), onClick: function () { addPlanToCart(false); } }))));
    return page;
  }

  function render() {
    closeMenus();
    var view;
    if (state.view === 'landing') view = renderLanding();
    else if (state.view === 'result') view = renderResult();
    else if (state.view === 'blocked') view = h('div', { className: 'fs3-landing' }, h('h1', { text: 'This check is for people 18 and over' }), h('p', { className: 'fs3-landing__sub', text: state.error || '' }));
    else if (state.view === 'boot') view = h('div', { className: 'fs3-boot' }, h('div', { className: 'fs3-spinner' }), h('p', { text: 'Loading your check…' }));
    else view = renderQuiz();

    var sheet = root.querySelector('.fs3-veil');
    root.textContent = '';
    root.appendChild(view);
    if (sheet) root.appendChild(sheet);                 // an open sheet survives a redraw
    root.setAttribute('data-fs3-view', state.view);
    syncCountdown();
  }
  function closeMenus() { var m = root.querySelector('.fs3-menu'); if (m) m.remove(); }

  // ── boot ──────────────────────────────────────────────────────────
  function loadLanding() {
    state.view = 'landing';
    return api('GET', '/landing').then(function (res) {
      if (res.ok) state.landing = res.data; else state.error = res.error.message;
      render();
      track('landing_viewed');
    });
  }

  function boot() {
    // She opened the page. Fired on EVERY boot — before any journey exists and
    // on all three paths below — so "she arrived" is counted the same way for
    // the WhatsApp invite and for the site's own button. An attempt is not
    // created until she presses Start, so counting attempts alone would drop
    // everyone who looked and left. landing_viewed is NOT a substitute: a
    // returning visitor never reaches loadLanding().
    // It carries the full source and the GA ids because someone who leaves
    // before Start has no other record of where she came from.
    var u = utm();
    var ga = readGa();
    track('page_opened', {
      utm_source: u.source || null, utm_medium: u.medium || null, utm_campaign: u.campaign || null,
      utm_content: u.content || null, utm_term: u.term || null, referrer: referrerPath(),
      ga_client_id: ga.clientId, ga_session_id: ga.sessionId,
    });

    refreshCartCount();
    if (store.get('fs3.attemptToken')) {
      return api('GET', '/attempts/current').then(function (res) {
        if (res.ok) return handleJourneyResponse(res.data);
        clearJourney();
        return store.get('fs3.sessionToken') ? startAttempt({ consent: consentPayload() }) : loadLanding();
      });
    }
    // Logged in on this device but no journey here: the server decides whether
    // she sees her result, continues another device's check, or starts fresh.
    if (store.get('fs3.sessionToken')) {
      return api('GET', '/auth/session').then(function (res) {
        if (res.ok && res.data.loggedIn) return startAttempt({ consent: consentPayload() || { given: true, version: 1 } });
        store.del('fs3.sessionToken');
        return loadLanding();
      });
    }
    return loadLanding();
  }

  boot();
})();
